import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, Switch, Tab, Tabs, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Tooltip,
  FormControlLabel, Select, MenuItem, InputLabel, FormControl, Drawer, Divider,
  useMediaQuery,
} from '@mui/material';
import AddIcon                  from '@mui/icons-material/Add';
import EditIcon                 from '@mui/icons-material/Edit';
import DeleteIcon               from '@mui/icons-material/Delete';
import TuneIcon                 from '@mui/icons-material/Tune';
import RefreshIcon              from '@mui/icons-material/Refresh';
import AccessTimeIcon           from '@mui/icons-material/AccessTime';
import SettingsIcon             from '@mui/icons-material/Settings';
import VisibilityIcon           from '@mui/icons-material/Visibility';
import PhotoLibraryIcon        from '@mui/icons-material/PhotoLibrary';
import CameraAltIcon           from '@mui/icons-material/CameraAlt';
import DeleteOutlineIcon       from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon       from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon          from '@mui/icons-material/ExpandMore';
import ExpandLessIcon          from '@mui/icons-material/ExpandLess';
import HomeIcon                from '@mui/icons-material/Home';
import ReceiptLongIcon         from '@mui/icons-material/ReceiptLong';
import RestaurantMenuIcon      from '@mui/icons-material/RestaurantMenu';
import StarIcon                from '@mui/icons-material/Star';
import BarChartIcon            from '@mui/icons-material/BarChart';
import PaletteIcon             from '@mui/icons-material/Palette';
import PaymentIcon             from '@mui/icons-material/Payment';
import ChevronRightIcon        from '@mui/icons-material/ChevronRight';
import EmojiEventsIcon         from '@mui/icons-material/EmojiEvents';
import AssessmentIcon          from '@mui/icons-material/Assessment';
import EventIcon               from '@mui/icons-material/Event';
import LocalOfferIcon          from '@mui/icons-material/LocalOffer';
import GroupIcon               from '@mui/icons-material/Group';
import FolderIcon              from '@mui/icons-material/Folder';
import DownloadIcon            from '@mui/icons-material/Download';
import ThumbUpIcon             from '@mui/icons-material/ThumbUp';
import ThumbDownIcon           from '@mui/icons-material/ThumbDown';
import FilterListIcon          from '@mui/icons-material/FilterList';
import MenuIcon                from '@mui/icons-material/Menu';
import CloseIcon               from '@mui/icons-material/Close';
import api                      from '../../services/api';
import restaurantService         from '../../services/restaurant.service';
import { fmtDateTimeTZ }        from '../../utils/date';
import MapboxAddressInput       from '../../components/MapboxAddressInput';
import StripeConnectSection     from '../../components/StripeConnectSection';

/* ── Palette : tons mats organiques ────────────────────────────────────────── */
const P = {
  bg:      '#E4F0F6',
  surface: '#E4F0F6',
  white:   '#ffffff',
  text:    '#1c1917',
  soft:    '#44403c',
  muted:   '#78716c',
  line:    'rgba(28,25,23,0.08)',
  lineFine:'rgba(28,25,23,0.05)',
  purple:  '#552583',
  purpleD: '#33154f',
  gold:    '#FDB927',
  green:   '#15803d',
  greenBg: 'rgba(34,197,94,0.06)',
  red:     '#b91c1c',
  redBg:   'rgba(239,68,68,0.05)',
  orange:  '#b45309',
  orangeBg:'rgba(245,158,11,0.06)',
  blue:    '#1d4ed8',
};

const GRAIN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E")`;

/* ── Status ────────────────────────────────────────────────────────────────── */
const STATUS = {
  pending:    { label: 'En attente',     color: P.orange, next: 'confirmed' },
  confirmed:  { label: 'Confirmée',      color: P.blue,   next: 'preparing' },
  preparing:  { label: 'Préparation',    color: P.purple, next: 'ready' },
  ready:      { label: 'Prête',          color: P.green,  next: null },
  picked_up:  { label: 'Récupérée',      color: P.green,  next: null },
  delivering: { label: 'En livraison',   color: '#0e7490',next: null },
  delivered:  { label: 'Livrée',         color: P.green,  next: null },
  cancelled:  { label: 'Annulée',        color: P.red,    next: null },
};
const getNextLabel = (nextStatus, orderType) => {
  if (nextStatus === 'ready') return orderType === 'click_and_collect' ? 'Prêt à retirer' : 'Prêt à livrer';
  return { confirmed: 'Confirmer', preparing: 'En préparation' }[nextStatus] || 'Avancer';
};

const fmt     = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN    = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtDate = fmtDateTimeTZ;

/* ── Shared styles ─────────────────────────────────────────────────────────── */
const micro = { color: P.muted, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' };
const mono  = { fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace" };
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px', background: 'transparent', fontSize: '0.86rem',
    '& fieldset': { borderColor: P.line, borderWidth: '0.5px' },
    '&:hover fieldset': { borderColor: 'rgba(85,37,131,0.25)' },
    '&.Mui-focused fieldset': { borderColor: P.purple, borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': { color: P.muted, fontSize: '0.82rem', fontWeight: 500 },
  '& .MuiInputLabel-root.Mui-focused': { color: P.purple },
};

/* ── StatusDot ─────────────────────────────────────────────────────────────── */
const StatusDot = ({ color, label }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7 }}>
    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color }}>{label}</Typography>
  </Box>
);

/* ── Stat (editorial KPI) ──────────────────────────────────────────────────── */
const Stat = ({ label, value, accent }) => (
  <Box>
    <Typography sx={{ ...mono, color: accent || P.text, fontWeight: 800, fontSize: '1.8rem', lineHeight: 1, letterSpacing: '-0.03em' }}>
      {value}
    </Typography>
    <Typography sx={{ ...micro, mt: 0.5 }}>{label}</Typography>
  </Box>
);

/* ── OrderRow ──────────────────────────────────────────────────────────────── */
const OrderRow = ({ order, onUpdateStatus, onCollected, loading }) => {
  const s = STATUS[order.order_status] || STATUS.pending;
  const isActive = ['pending','confirmed','preparing'].includes(order.order_status);
  const isPending = order.order_status === 'pending';
  const [pickupCode, setPickupCode] = React.useState('');
  const [pickupLoading, setPickupLoading] = React.useState(false);
  const [pickupError, setPickupError] = React.useState('');

  const confirmPickup = async () => {
    if (!pickupCode.trim()) return;
    setPickupLoading(true);
    setPickupError('');
    try {
      await api.post(`/restaurants/orders/${order.id}/verify-delivery`, { code: pickupCode.trim() });
      if (onCollected) onCollected(order.id);
    } catch (err) {
      setPickupError(err.response?.data?.message || 'Code incorrect');
    } finally {
      setPickupLoading(false);
    }
  };

  return (
    <Box sx={{
      py: 2.5, px: 3,
      borderBottom: `0.5px solid ${P.lineFine}`,
      background: isPending ? P.orangeBg : 'transparent',
      borderLeft: isPending ? `3px solid ${P.orange}` : '3px solid transparent',
      transition: 'background 0.15s',
      '&:hover': { background: isPending ? P.orangeBg : 'rgba(28,25,23,0.015)' },
    }}>
      {/* Line 1: number + status + amount */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ ...mono, fontWeight: 800, fontSize: '0.88rem', color: P.text }}>
            #{order.order_number}
          </Typography>
          <StatusDot color={s.color} label={s.label} />
          {order.order_type === 'click_and_collect' && (
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: P.blue, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Retrait
            </Typography>
          )}
        </Box>
        <Typography sx={{ ...mono, fontWeight: 800, fontSize: '1rem', color: P.text }}>
          {fmt(order.total_amount)}&thinsp;EUR
        </Typography>
      </Box>

      {/* Line 2: client + date */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 24, height: 24, borderRadius: '50%', background: P.lineFine,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.62rem', fontWeight: 800, color: P.soft,
          }}>
            {(order.customer_name || 'C')[0]}
          </Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: P.soft }}>
            {order.customer_name}
          </Typography>
          {order.customer_phone && (
            <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>{order.customer_phone}</Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: P.muted }}>{fmtDate(order.created_at)}</Typography>
      </Box>

      {/* Line 3: items */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: isActive ? 2 : 0 }}>
        {(order.items || []).map((item, i) => (
          <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, py: 0.3, px: 1, border: `0.5px solid ${P.line}`, borderRadius: '4px', alignSelf: 'flex-start' }}>
              <Typography sx={{ ...mono, fontSize: '0.68rem', fontWeight: 800, color: P.purple }}>{item.quantity}x</Typography>
              <Typography sx={{ fontSize: '0.74rem', color: P.soft }}>{item.name}</Typography>
            </Box>
            {Array.isArray(item.options_chosen) && item.options_chosen.length > 0 && (
              <Typography sx={{ fontSize: '0.68rem', color: P.muted, ml: 1.2, lineHeight: 1.5 }}>
                {item.options_chosen.map((g, gi) => `${g.group_name}: ${g.choices.map(c => c.name).join(', ')}`).join(' • ')}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* Actions */}
      {isActive && (
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {isPending && (
            <Button size="small" onClick={() => onUpdateStatus(order.id, 'cancelled')} disabled={loading === order.id}
              sx={{
                py: 0.6, px: 2, borderRadius: '5px', textTransform: 'none',
                fontWeight: 700, fontSize: '0.76rem', color: P.red,
                border: `0.5px solid ${P.red}`, background: 'transparent',
                '&:hover': { background: P.redBg },
              }}>
              Refuser
            </Button>
          )}
          {s.next && (
            <Button size="small" onClick={() => onUpdateStatus(order.id, s.next)} disabled={loading === order.id}
              sx={{
                py: 0.6, px: 2.5, borderRadius: '5px', textTransform: 'none',
                fontWeight: 700, fontSize: '0.76rem', color: P.white,
                background: P.text, border: 'none',
                '&:hover': { background: P.text, opacity: 0.85 },
                '&:disabled': { opacity: 0.3, background: P.text, color: P.white },
              }}>
              {loading === order.id ? <CircularProgress size={14} sx={{ color: P.white }} /> : getNextLabel(s.next, order.order_type)}
            </Button>
          )}
        </Box>
      )}
      {order.order_status === 'ready' && order.order_type !== 'click_and_collect' && (
        <Box sx={{ py: 1, px: 2, borderLeft: `3px solid ${P.green}`, background: P.greenBg }}>
          <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: P.green }}>En attente du livreur</Typography>
        </Box>
      )}
      {order.order_status === 'ready' && order.order_type === 'click_and_collect' && (
        <Box sx={{ py: 1.5, px: 2, borderLeft: `3px solid ${P.green}`, background: P.greenBg }}>
          <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: P.green, mb: 1 }}>
            En attente du client — saisir le code de retrait
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small" placeholder="Code à 6 chiffres"
              value={pickupCode} onChange={e => { setPickupCode(e.target.value); setPickupError(''); }}
              inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.15em', background: 'white' } }}
            />
            <Button size="small" onClick={confirmPickup} disabled={pickupLoading || pickupCode.length < 6}
              sx={{ py: 0.6, px: 2, borderRadius: '5px', textTransform: 'none', fontWeight: 700, fontSize: '0.76rem', background: P.green, color: 'white', '&:hover': { background: '#16a34a' }, '&:disabled': { opacity: 0.4 } }}>
              {pickupLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : 'Confirmer'}
            </Button>
          </Box>
          {pickupError && <Typography sx={{ fontSize: '0.72rem', color: P.red, mt: 0.8, fontWeight: 600 }}>{pickupError}</Typography>}
        </Box>
      )}
    </Box>
  );
};

/* ── IbanTab ───────────────────────────────────────────────────────────────── */
const IbanTab = () => {
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get('/connect/iban').then(r => { setCurrent(r.data); if (r.data.bic) setBic(r.data.bic); }).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!iban.trim()) { setMsg({ type: 'error', text: 'Veuillez saisir votre IBAN' }); return; }
    setSaving(true); setMsg(null);
    try {
      await api.post('/connect/iban', { iban: iban.trim(), bic: bic.trim() });
      setMsg({ type: 'success', text: 'IBAN enregistré' });
      const r = await api.get('/connect/iban'); setCurrent(r.data); setIban('');
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'Erreur enregistrement' }); }
    setSaving(false);
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 4, md: 6 }, alignItems: 'start' }}>
      {/* Left: form */}
      <Box>
        <Typography sx={{ ...micro, mb: 3 }}>Coordonnées bancaires</Typography>

        {msg && (
          <Box sx={{ mb: 3, py: 1.2, px: 2, borderLeft: `3px solid ${msg.type === 'success' ? P.green : P.red}`, background: msg.type === 'success' ? P.greenBg : P.redBg }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: msg.type === 'success' ? P.green : P.red }}>{msg.text}</Typography>
          </Box>
        )}

        {current?.has_iban && (
          <Box sx={{ mb: 3, py: 1.5, px: 2, borderLeft: `3px solid ${P.green}`, background: P.greenBg }}>
            <Typography sx={{ ...micro, mb: 0.5 }}>IBAN enregistré</Typography>
            <Typography sx={{ ...mono, fontSize: '0.88rem', fontWeight: 800, color: P.text, letterSpacing: '0.04em' }}>
              {current.iban}
            </Typography>
            {current.bic && <Typography sx={{ fontSize: '0.72rem', color: P.muted, mt: 0.3 }}>BIC : {current.bic}</Typography>}
          </Box>
        )}

        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: P.text, mb: 2 }}>
          {current?.has_iban ? 'Modifier l\'IBAN' : 'Ajouter mon IBAN'}
        </Typography>

        <TextField fullWidth label="IBAN" value={iban} onChange={e => setIban(e.target.value)}
          placeholder="FR76 3000 6000 0112 3456 7890 189"
          inputProps={{ style: { ...mono, letterSpacing: '0.06em' } }}
          sx={{ ...fieldSx, mb: 2 }} />
        <TextField fullWidth label="BIC / SWIFT (optionnel)" value={bic} onChange={e => setBic(e.target.value)}
          placeholder="BNPAFRPPXXX"
          inputProps={{ style: { ...mono } }}
          sx={{ ...fieldSx, mb: 3 }} />

        <Button onClick={handleSave} disabled={saving}
          sx={{
            py: 1.1, px: 3, borderRadius: '5px', textTransform: 'none',
            fontWeight: 700, fontSize: '0.82rem', background: P.text, color: P.white,
            '&:hover': { background: P.text, opacity: 0.85 },
            '&:disabled': { opacity: 0.3, background: P.text, color: P.white },
          }}>
          {saving ? 'Enregistrement...' : 'Enregistrer l\'IBAN'}
        </Button>
      </Box>

      {/* Right: explainer */}
      <Box sx={{ pt: { xs: 0, md: 0.5 } }}>
        <Typography sx={{ ...micro, mb: 3 }}>Comment ça marche</Typography>
        {[
          { n: '01', title: 'Client passe commande', desc: 'Paiement en ligne par carte au moment de la commande.' },
          { n: '02', title: 'Vous préparez', desc: 'Usafiri encaisse le paiement pendant ce temps.' },
          { n: '03', title: 'Livraison confirmée', desc: 'Le livreur scanne le code, le virement est déclenché.' },
          { n: '04', title: 'Vous recevez 85%', desc: 'Du sous-total articles, sous 2 à 7 jours ouvrés.', accent: true },
        ].map((step, i) => (
          <Box key={i} sx={{
            display: 'flex', gap: 2, py: 1.8,
            borderBottom: i < 3 ? `0.5px solid ${P.lineFine}` : 'none',
          }}>
            <Typography sx={{ ...mono, fontSize: '0.68rem', fontWeight: 800, color: step.accent ? P.gold : P.muted, mt: 0.2 }}>
              {step.n}
            </Typography>
            <Box>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: step.accent ? P.purple : P.text, mb: 0.2 }}>
                {step.title}
              </Typography>
              <Typography sx={{ fontSize: '0.74rem', color: P.muted, lineHeight: 1.5 }}>{step.desc}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
export default function RestaurantPartner() {
  const pollRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isTablet = useMediaQuery('(max-width:899px)');
  const [myRestaurants, setMyRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderLoad, setOrderLoad] = useState(null);
  const [menuLoad, setMenuLoad] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [menuDialog, setMenuDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [itemForm, setItemForm] = useState({ name:'', category:'', description:'', price:'', preparation_time:'15', is_vegetarian: false, is_available: true, image_url: '' });
  const [drinkDialog, setDrinkDialog] = useState(false);
  const [drinkForm, setDrinkForm] = useState({ name:'', subcategory:'', temperature:'froid', volume:'', description:'', price:'', image_url:'', is_available: true });
  const [optionsDialog, setOptionsDialog] = useState(false);
  const [optionsItem, setOptionsItem] = useState(null);
  const [optionsBusy, setOptionsBusy] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [restForm, setRestForm] = useState({});
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingDish, setUploadingDish] = useState(false);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [collapsedCats, setCollapsedCats] = useState({});
  const dragCat = useRef(null);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewLoad, setReviewLoad] = useState(false);
  const [replyDialog, setReplyDialog] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySaving, setReplySaving] = useState(false);
  const [reviewSubTab, setReviewSubTab] = useState(0);
  const [reviewFilter, setReviewFilter] = useState({ stars: null, hasComment: false, replied: null });
  const [topItems, setTopItems] = useState([]);
  const [topLoad, setTopLoad] = useState(false);
  const [specialHours, setSpecialHours] = useState([]);
  const [specialHoursLoad, setSpecialHoursLoad] = useState(false);
  const [specialHoursDialog, setSpecialHoursDialog] = useState(false);
  const [shForm, setShForm] = useState({ date: '', reason: '', is_closed: true, open_time: '', close_time: '' });
  const [promos, setPromos] = useState([]);
  const [promosLoad, setPromosLoad] = useState(false);
  const [promoDialog, setPromoDialog] = useState(false);
  const [promoForm, setPromoForm] = useState({ code: '', discount_type: 'percent', discount_value: '', min_order: '', max_uses: '', expires_at: '' });
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoad, setAnnouncementsLoad] = useState(false);
  const [announcementDialog, setAnnouncementDialog] = useState(false);
  const [editAnnouncement, setEditAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', content: '', type: 'info', starts_at: '', ends_at: '' });
  const [teamMembers, setTeamMembers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  // Tab 13 — option dialog states
  const [optionDialogItem, setOptionDialogItem] = React.useState(null);
  const [optionLoading, setOptionLoading] = React.useState(false);
  const [newGroupMode, setNewGroupMode] = React.useState(false);
  const [newGroupName, setNewGroupName] = React.useState('');
  const [newGroupType, setNewGroupType] = React.useState('single');
  const [addingChoiceGroupId, setAddingChoiceGroupId] = React.useState(null);
  const [newChoiceName, setNewChoiceName] = React.useState('');
  const [newChoicePrice, setNewChoicePrice] = React.useState('0');

  const toggleCatCollapse = (cat) => setCollapsedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const showSuccess = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3500); };
  const showError   = (m) => { setError(m);   setTimeout(() => setError(''),   4000); };

  const params = (extra = {}) => selectedRestaurantId ? { restaurant_id: selectedRestaurantId, ...extra } : extra;

  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/restaurants/partner/dashboard', { params: params() });
      setDashboard(data);
      if (data.restaurant) {
        const r = { ...data.restaurant };
        if (typeof r.time_slots === 'string')  { try { r.time_slots   = JSON.parse(r.time_slots);   } catch {} }
        if (typeof r.opening_days === 'string') { try { r.opening_days = JSON.parse(r.opening_days); } catch {} }
        setRestForm(r);
      }
    } catch { if (!silent) showError('Erreur chargement'); }
    finally { if (!silent) setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurantId]);

  const loadOrders = useCallback(async (silent = false) => {
    try { const { data } = await api.get('/restaurants/partner/orders', { params: params() }); setOrders(data.orders || []); }
    catch { if (!silent) showError('Erreur chargement commandes'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurantId]);

  const loadMenu = useCallback(async () => {
    setMenuLoad(true);
    try { const { data } = await api.get('/restaurants/partner/menu', { params: params() }); setMenuItems(data.items || []); }
    catch { showError('Erreur chargement menu'); }
    finally { setMenuLoad(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurantId]);

  const loadReviews = useCallback(async () => {
    setReviewLoad(true);
    try {
      const { data } = await api.get('/restaurants/partner/reviews');
      setReviews(data.reviews || []);
      setReviewStats(data.stats || null);
    } catch { showError('Erreur chargement avis'); }
    finally { setReviewLoad(false); }
  }, []);

  const loadTopItems = useCallback(async () => {
    setTopLoad(true);
    try {
      const { data } = await api.get('/restaurants/partner/top-items');
      setTopItems(data.items || []);
    } catch {}
    finally { setTopLoad(false); }
  }, []);

  const loadPromos = useCallback(async () => {
    setPromosLoad(true);
    try {
      const { data } = await api.get('/restaurants/partner/promos');
      setPromos(data.promos || []);
    } catch {}
    finally { setPromosLoad(false); }
  }, []);

  const loadAnnouncements = useCallback(async () => {
    setAnnouncementsLoad(true);
    try {
      const { data } = await api.get('/restaurants/partner/announcements');
      setAnnouncements(data.announcements || []);
    } catch {}
    finally { setAnnouncementsLoad(false); }
  }, []);

  const deleteAnnouncement = async (id) => {
    try {
      await api.delete(`/restaurants/partner/announcements/${id}`);
      setAnnouncements(a => a.filter(x => x.id !== id));
      showSuccess('Annonce supprimée');
    } catch { showError('Erreur suppression'); }
  };

  const saveAnnouncement = async () => {
    try {
      if (editAnnouncement) {
        await api.put(`/restaurants/partner/announcements/${editAnnouncement.id}`, announcementForm);
        showSuccess('Annonce modifiée');
      } else {
        await api.post('/restaurants/partner/announcements', announcementForm);
        showSuccess('Annonce publiée');
      }
      setAnnouncementDialog(false);
      loadAnnouncements();
    } catch { showError('Erreur enregistrement'); }
  };

  const deletePromo = async (id) => {
    try {
      await api.delete(`/restaurants/partner/promos/${id}`);
      setPromos(p => p.filter(x => x.id !== id));
      showSuccess('Promo supprimée');
    } catch { showError('Erreur suppression'); }
  };

  const exportOrdersCSV = async () => {
    setExportLoading(true);
    try {
      const { data } = await api.get('/restaurants/partner/orders');
      const rows = (data.orders || []).map(o => [
        o.order_number, o.customer_name || '', o.total_amount, o.order_status,
        o.order_type, new Date(o.created_at).toLocaleDateString('fr-FR'),
      ]);
      const csv = [['#Commande','Client','Montant','Statut','Type','Date'], ...rows]
        .map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'commandes.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { showError('Erreur export'); }
    finally { setExportLoading(false); }
  };

  // Charger la liste des restaurants du owner au montage
  useEffect(() => {
    restaurantService.getMyRestaurants().then(d => {
      if (d.restaurants?.length) setMyRestaurants(d.restaurants);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadDashboard(); loadOrders(); loadMenu(); }, [loadDashboard, loadOrders, loadMenu]);
  useEffect(() => { if (tab === 1 || tab === 13) loadMenu(); }, [tab, loadMenu]);
  useEffect(() => { if (tab === 5) loadReviews(); }, [tab, loadReviews]);
  useEffect(() => { if (tab === 7) loadTopItems(); }, [tab, loadTopItems]);
  useEffect(() => { if (tab === 9) { loadPromos(); loadAnnouncements(); } }, [tab, loadPromos, loadAnnouncements]);
  useEffect(() => {
    pollRef.current = setInterval(() => { loadOrders(true); loadDashboard(true); }, 30000);
    return () => clearInterval(pollRef.current);
  }, [loadDashboard, loadOrders]);

  const handleToggle = async () => {
    try {
      const { data } = await api.put('/restaurants/partner/toggle');
      setDashboard(prev => ({ ...prev, restaurant: { ...prev.restaurant, is_open_manual: data.is_open } }));
      showSuccess(data.is_open ? 'Restaurant réactivé — les horaires s\'appliquent à nouveau' : 'Restaurant fermé manuellement');
    } catch { showError('Erreur mise a jour statut'); }
  };

  const handleToggleDelivery = async () => {
    try {
      const { data } = await api.put('/restaurants/partner/toggle-delivery');
      setDashboard(prev => ({ ...prev, restaurant: { ...prev.restaurant, delivery_enabled: data.delivery_enabled } }));
      showSuccess(data.delivery_enabled ? 'Livraison réactivée' : 'Livraison suspendue');
    } catch { showError('Erreur mise à jour livraison'); }
  };

  const handleOrderStatus = async (orderId, status) => {
    setOrderLoad(orderId);
    try {
      await api.put(`/restaurants/partner/orders/${orderId}/status`, { status });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, order_status: status } : o));
      const labels = { confirmed: 'Commande confirmee', preparing: 'En preparation', ready: 'Commande prete', cancelled: 'Commande annulee' };
      showSuccess(labels[status] || 'Statut mis a jour');
      loadDashboard(true);
    } catch { showError('Erreur mise a jour commande'); }
    finally { setOrderLoad(null); }
  };

  const handleOrderCollected = (orderId) => {
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, order_status: 'collected' } : o));
    showSuccess('Retrait confirmé — commande terminée');
    loadDashboard(true);
  };

  const openMenuDialog = (item = null, defaultCategory = '') => {
    if (item) {
      setEditItem(item);
      setItemForm({ name: item.name, category: item.category, description: item.description||'', price: item.price, preparation_time: item.preparation_time||15, is_vegetarian: !!item.is_vegetarian, is_available: !!item.is_available, image_url: item.image_url||'' });
    } else {
      setEditItem(null);
      setItemForm({ name:'', category: defaultCategory, description:'', price:'', preparation_time:'15', is_vegetarian: false, is_available: true, image_url: '' });
    }
    setMenuDialog(true);
  };

  const handleSaveItem = async () => {
    if (!itemForm.name || !itemForm.category || !itemForm.price) { showError('Nom, categorie et prix requis'); return; }
    if (uploadingDish) { showError('Attendez la fin du chargement de la photo'); return; }
    setMenuLoad(true);
    try {
      if (editItem) {
        await api.put(`/restaurants/partner/menu/${editItem.id}`, itemForm);
        // Mettre à jour le state local avec les valeurs du formulaire (le backend ne renvoie pas l'item)
        setMenuItems(prev => prev.map(i => i.id === editItem.id
          ? { ...i, ...itemForm, price: parseFloat(itemForm.price) }
          : i
        ));
        showSuccess('Article mis a jour');
      } else {
        const { data } = await api.post('/restaurants/partner/menu', itemForm);
        setMenuItems(prev => [...prev, { ...(data.item || itemForm), option_groups: [] }]);
        showSuccess('Article ajoute');
      }
      setMenuDialog(false);
    } catch { showError('Erreur sauvegarde article'); }
    finally { setMenuLoad(false); }
  };

  const openDrinkDialog = (item = null) => {
    if (item) {
      setDrinkForm({ name: item.name, subcategory: '', temperature: 'froid', volume: '', description: item.description||'', price: item.price, image_url: item.image_url||'', is_available: !!item.is_available });
    } else {
      setDrinkForm({ name:'', subcategory:'', temperature:'froid', volume:'', description:'', price:'', image_url:'', is_available: true });
    }
    setDrinkDialog(true);
  };

  const handleSaveDrink = async () => {
    if (!drinkForm.name || !drinkForm.price) { showError('Nom et prix requis'); return; }
    if (uploadingDish) { showError('Attendez la fin du chargement de la photo'); return; }
    setMenuLoad(true);
    try {
      const descParts = [];
      if (drinkForm.subcategory) descParts.push(drinkForm.subcategory);
      if (drinkForm.volume)      descParts.push(drinkForm.volume);
      if (drinkForm.temperature) descParts.push(drinkForm.temperature === 'froid' ? '🧊 Froid' : '☕ Chaud');
      if (drinkForm.description) descParts.push(drinkForm.description);
      const payload = {
        name: drinkForm.name,
        category: 'Boissons',
        description: descParts.join(' · '),
        price: drinkForm.price,
        image_url: drinkForm.image_url,
        is_available: drinkForm.is_available,
        is_vegetarian: true,
        preparation_time: 2,
      };
      const { data } = await api.post('/restaurants/partner/menu', payload);
      setMenuItems(prev => [...prev, { ...(data.item || payload), option_groups: [] }]);
      showSuccess('Boisson ajoutée');
      setDrinkDialog(false);
    } catch { showError('Erreur sauvegarde boisson'); }
    finally { setMenuLoad(false); }
  };

  const handleDeleteItem = async (itemId) => {
    setMenuLoad(true);
    try { await api.delete(`/restaurants/partner/menu/${itemId}`); setMenuItems(prev => prev.filter(i => i.id !== itemId)); showSuccess('Article supprime'); }
    catch { showError('Erreur suppression'); }
    finally { setMenuLoad(false); }
  };

  const handleToggleAvailable = async (item) => {
    try {
      const { data } = await api.put(`/restaurants/partner/menu/${item.id}`, { ...item, is_available: !item.is_available });
      // Preserver option_groups absent de la reponse backend
      setMenuItems(prev => prev.map(i => i.id === item.id ? { ...data.item, option_groups: i.option_groups || [] } : i));
    } catch { showError('Erreur disponibilite'); }
  };

  // ── Options de personnalisation ───────────────────────────────────────────
  const openOptionsDialog = (item) => {
    setOptionsItem({ ...item, option_groups: item.option_groups || [] });
    setOptionsDialog(true);
  };

  const refreshItemInList = (updated) => {
    setMenuItems(prev => prev.map(i => i.id === updated.id ? updated : i));
    setOptionsItem(updated);
  };

  const handleAddGroup = async () => {
    if (!optionsItem) return;
    setOptionsBusy(true);
    try {
      const res = await restaurantService.createOptionGroup(optionsItem.id, {
        name: 'Nouveau groupe', selection_type: 'single', min_select: 0, max_select: 1, display_order: (optionsItem.option_groups?.length || 0),
      });
      const newGroup = { id: res.group_id, name: 'Nouveau groupe', selection_type: 'single', min_select: 0, max_select: 1, display_order: optionsItem.option_groups.length, choices: [] };
      refreshItemInList({ ...optionsItem, option_groups: [...optionsItem.option_groups, newGroup] });
    } catch { showError('Erreur ajout groupe'); }
    finally { setOptionsBusy(false); }
  };

  const handleUpdateGroup = async (groupId, patch) => {
    const group = optionsItem.option_groups.find(g => g.id === groupId);
    const updated = { ...group, ...patch };
    setOptionsBusy(true);
    try {
      await restaurantService.updateOptionGroup(groupId, {
        name: updated.name, selection_type: updated.selection_type, min_select: updated.min_select, max_select: updated.max_select, display_order: updated.display_order,
      });
      refreshItemInList({ ...optionsItem, option_groups: optionsItem.option_groups.map(g => g.id === groupId ? updated : g) });
    } catch { showError('Erreur mise a jour groupe'); }
    finally { setOptionsBusy(false); }
  };

  const handleDeleteGroup = async (groupId) => {
    setOptionsBusy(true);
    try {
      await restaurantService.deleteOptionGroup(groupId);
      refreshItemInList({ ...optionsItem, option_groups: optionsItem.option_groups.filter(g => g.id !== groupId) });
    } catch { showError('Erreur suppression groupe'); }
    finally { setOptionsBusy(false); }
  };

  const handleAddChoice = async (groupId) => {
    setOptionsBusy(true);
    try {
      const group = optionsItem.option_groups.find(g => g.id === groupId);
      const res = await restaurantService.createOptionChoice(groupId, { name: 'Nouveau choix', price_delta: 0, display_order: group.choices.length });
      const newChoice = { id: res.choice_id, name: 'Nouveau choix', price_delta: 0, display_order: group.choices.length };
      refreshItemInList({ ...optionsItem, option_groups: optionsItem.option_groups.map(g => g.id === groupId ? { ...g, choices: [...g.choices, newChoice] } : g) });
    } catch { showError('Erreur ajout choix'); }
    finally { setOptionsBusy(false); }
  };

  const handleUpdateChoice = async (groupId, choiceId, patch) => {
    const group = optionsItem.option_groups.find(g => g.id === groupId);
    const choice = group.choices.find(c => c.id === choiceId);
    const updated = { ...choice, ...patch };
    setOptionsBusy(true);
    try {
      await restaurantService.updateOptionChoice(choiceId, { name: updated.name, price_delta: parseFloat(updated.price_delta) || 0, display_order: updated.display_order });
      refreshItemInList({ ...optionsItem, option_groups: optionsItem.option_groups.map(g => g.id === groupId ? { ...g, choices: g.choices.map(c => c.id === choiceId ? updated : c) } : g) });
    } catch { showError('Erreur mise a jour choix'); }
    finally { setOptionsBusy(false); }
  };

  const handleDeleteChoice = async (groupId, choiceId) => {
    setOptionsBusy(true);
    try {
      await restaurantService.deleteOptionChoice(choiceId);
      refreshItemInList({ ...optionsItem, option_groups: optionsItem.option_groups.map(g => g.id === groupId ? { ...g, choices: g.choices.filter(c => c.id !== choiceId) } : g) });
    } catch { showError('Erreur suppression choix'); }
    finally { setOptionsBusy(false); }
  };

  // Templates pre-remplis pour demarrer rapidement
  const handleApplyTemplate = async (template) => {
    if (!optionsItem) return;
    setOptionsBusy(true);
    try {
      const res = await restaurantService.createOptionGroup(optionsItem.id, {
        name: template.name,
        selection_type: template.selection_type,
        min_select: template.min_select,
        max_select: template.max_select,
        display_order: optionsItem.option_groups.length,
      });
      const groupId = res.group_id;
      const newChoices = [];
      for (let i = 0; i < template.choices.length; i++) {
        const c = template.choices[i];
        const cRes = await restaurantService.createOptionChoice(groupId, { name: c.name, price_delta: c.price_delta, display_order: i });
        newChoices.push({ id: cRes.choice_id, name: c.name, price_delta: c.price_delta, display_order: i });
      }
      const newGroup = {
        id: groupId, name: template.name, selection_type: template.selection_type,
        min_select: template.min_select, max_select: template.max_select,
        display_order: optionsItem.option_groups.length, choices: newChoices,
      };
      refreshItemInList({ ...optionsItem, option_groups: [...optionsItem.option_groups, newGroup] });
    } catch { showError('Erreur création groupe'); }
    finally { setOptionsBusy(false); }
  };

  const OPTION_TEMPLATES = [
    {
      key: 'size', label: 'Taille', emoji: 'Ø',
      name: 'Quelle taille ?', selection_type: 'single', min_select: 1, max_select: 1,
      choices: [
        { name: 'Moyenne', price_delta: 0 },
        { name: 'Grande', price_delta: 3 },
        { name: 'XL', price_delta: 5 },
      ],
    },
    {
      key: 'supp', label: 'Suppléments', emoji: '+',
      name: 'Suppléments', selection_type: 'multiple', min_select: 0, max_select: 5,
      choices: [
        { name: 'Fromage extra', price_delta: 1 },
        { name: 'Olives', price_delta: 0.5 },
        { name: 'Champignons', price_delta: 0.5 },
      ],
    },
    {
      key: 'sauce', label: 'Sauce', emoji: '~',
      name: 'Quelle sauce ?', selection_type: 'single', min_select: 1, max_select: 1,
      choices: [
        { name: 'Tomate', price_delta: 0 },
        { name: 'Barbecue', price_delta: 0 },
        { name: 'Blanche', price_delta: 0 },
      ],
    },
  ];

  const handleSaveSettings = async () => {
    try {
      await api.put('/restaurants/partner/update', restForm);
      showSuccess('Informations mises a jour');
      setSettingsDialog(false);
      await loadDashboard(false);
    } catch (err) { showError(err.response?.data?.message || 'Erreur sauvegarde'); }
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSize) { showError('Image trop volumineuse (max 5 Mo)'); return; }
    if (!['image/jpeg','image/png','image/webp'].includes(file.type)) { showError('Format accepte : JPG, PNG ou WebP'); return; }

    const setUploading = type === 'cover' ? setUploadingCover : setUploadingLogo;
    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await api.post('/restaurants/partner/upload-image', { file: base64, type });
      if (data.url) {
        const field = type === 'cover' ? 'cover_image' : 'logo_url';
        setRestForm(p => ({ ...p, [field]: data.url }));
        setDashboard(prev => prev ? { ...prev, restaurant: { ...prev.restaurant, [field]: data.url } } : prev);
        showSuccess(type === 'cover' ? 'Image de couverture mise a jour' : 'Logo mis a jour');
      }
    } catch (err) { showError(err.response?.data?.message || 'Erreur upload image'); }
    finally { setUploading(false); }
  };

  const handleDishImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { showError('Image trop volumineuse (max 5 Mo)'); return; }
    if (!['image/jpeg','image/png','image/webp'].includes(file.type)) { showError('Format accepté : JPG, PNG ou WebP'); return; }
    setUploadingDish(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await api.post('/restaurants/partner/upload-image', { file: base64, type: 'dish' });
      if (data.url) setItemForm(p => ({ ...p, image_url: data.url }));
    } catch (err) { showError(err.response?.data?.message || 'Erreur upload image'); }
    finally { setUploadingDish(false); }
  };

  /* ── Catégories triées ───────────────────────────────────────────────────── */
  const rawCategories = [...new Set(menuItems.map(i => i.category).filter(Boolean))];
  const categories = [
    ...categoryOrder.filter(c => rawCategories.includes(c)),
    ...rawCategories.filter(c => !categoryOrder.includes(c)),
  ];

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    setCategoryOrder(prev => [
      ...prev.filter(c => rawCategories.includes(c)),
      ...rawCategories.filter(c => !prev.includes(c)),
    ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems]);

  const handleCatDragStart = (e, cat) => {
    dragCat.current = cat;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', cat);
  };
  const handleCatDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleCatDrop = (e, targetCat) => {
    e.preventDefault();
    const from = dragCat.current;
    if (!from || from === targetCat) return;
    setCategoryOrder(prev => {
      const next = [...prev];
      const fi = next.indexOf(from);
      const ti = next.indexOf(targetCat);
      if (fi === -1 || ti === -1) return prev;
      next.splice(fi, 1);
      next.splice(ti, 0, from);
      return next;
    });
    dragCat.current = null;
  };

  /* ── Loading ─────────────────────────────────────────────────────────────── */
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: P.bg }}>
      <CircularProgress size={24} sx={{ color: P.purple }} />
    </Box>
  );

  const resto = dashboard?.restaurant;
  const stats = dashboard?.stats || {};
  const pendingOrders = orders.filter(o => o.order_status === 'pending');
  const activeOrders = orders.filter(o => ['pending','confirmed','preparing','ready'].includes(o.order_status));
  const completedOrders = orders.filter(o => ['delivered','collected','cancelled'].includes(o.order_status));

  /* ── No restaurant yet ───────────────────────────────────────────────────── */
  if (!resto) return (
    <Box sx={{ minHeight: '100vh', background: P.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
      <Box sx={{ position: 'fixed', inset: 0, backgroundImage: GRAIN, backgroundRepeat: 'repeat', backgroundSize: '200px', pointerEvents: 'none' }} />
      <Box sx={{ textAlign: 'center', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        <Typography sx={{ ...micro, mb: 2 }}>Espace restaurateur</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', letterSpacing: '-0.03em', color: P.text, lineHeight: 1.1, mb: 1.5 }}>
          Creez votre restaurant
        </Typography>
        <Typography sx={{ fontSize: '0.84rem', color: P.muted, lineHeight: 1.6, mb: 4 }}>
          Vous n'avez pas encore configure votre restaurant sur Usafiri.
        </Typography>
        <Button onClick={() => setSettingsDialog(true)}
          sx={{
            py: 1.1, px: 3.5, borderRadius: '5px', textTransform: 'none',
            fontWeight: 700, fontSize: '0.84rem', background: P.text, color: P.white,
            '&:hover': { background: P.text, opacity: 0.85 },
          }}>
          Commencer
        </Button>
        {/* Settings dialog rendered below */}
      </Box>
      {renderSettingsDialog()}
    </Box>
  );

  const deliveryRate = stats.total_orders > 0 ? Math.round((stats.delivered_orders / stats.total_orders) * 100) : 0;
  const cancelRate = stats.total_orders > 0 ? Math.round((stats.cancelled_orders / stats.total_orders) * 100) : 0;

  /* ── Settings dialog (shared) ────────────────────────────────────────────── */
  function renderSettingsDialog() {
    return (
      <Dialog open={settingsDialog} onClose={() => setSettingsDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '8px', border: `0.5px solid ${P.line}` } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: P.text, pb: 0, pt: 2.5, px: 3 }}>
          Informations du restaurant
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: '16px !important' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth label="Nom du restaurant" size="small" value={restForm.name||''} onChange={e => setRestForm(p=>({...p,name:e.target.value}))} sx={fieldSx} />
            </Box>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Type de cuisine</InputLabel>
              <Select label="Type de cuisine" value={restForm.cuisine_type||''} onChange={e => setRestForm(p=>({...p,cuisine_type:e.target.value}))}>
                {[
                  'Pizzéria','Fast food','Burgers','Sushis & Japonais','Cuisine africaine',
                  'Cuisine créole','Cuisine française','Cuisine italienne','Cuisine asiatique',
                  'Cuisine chinoise','Cuisine indienne','Cuisine thaïlandaise','Cuisine mexicaine',
                  'Cuisine libanaise','Cuisine marocaine','Pâtisserie & Desserts','Boulangerie',
                  'Sandwichs & Wraps','Salades & Healthy','Végétarien & Vegan',
                  'Fruits de mer & Poissons','Grillades & Viandes','Snack','Café & Brunch','Divers',
                ].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Ville" size="small" value={restForm.city||''} onChange={e => setRestForm(p=>({...p,city:e.target.value}))} sx={fieldSx} />
            <Box sx={{ gridColumn: '1 / -1' }}>
              <MapboxAddressInput label="Adresse" placeholder="Adresse du restaurant" value={restForm.address || ''}
                onSelect={(result) => { if (!result) return; setRestForm(p => ({ ...p, address: result.fullAddress || result.place_name || result.address, city: result.city || p.city, latitude: result.latitude, longitude: result.longitude })); }}
                showGeolocate />
            </Box>
            <TextField fullWidth label="Telephone" size="small" value={restForm.phone||''} onChange={e => setRestForm(p=>({...p,phone:e.target.value}))} sx={fieldSx} />
            <TextField fullWidth label="Email" size="small" value={restForm.email||''} onChange={e => setRestForm(p=>({...p,email:e.target.value}))} sx={fieldSx} />
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: P.soft, mb: 1.2 }}>Horaires par jour</Typography>
              {(() => {
                const DAYS = ['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche'];
                const DEFAULT_OPEN = restForm.opening_time || '09:00';
                const DEFAULT_CLOSE = restForm.closing_time || '22:00';
                const ws = restForm.weekly_schedule || {};
                const getDay = (day) => ws[day] || { closed: false, open: DEFAULT_OPEN, close: DEFAULT_CLOSE };
                const setDay = (day, patch) => {
                  const current = getDay(day);
                  setRestForm(p => ({ ...p, weekly_schedule: { ...(p.weekly_schedule || {}), [day]: { ...current, ...patch } } }));
                };
                const hasSlot2 = (d) => !!(d.open2 && d.close2);
                const addSlot2 = (day) => setDay(day, { open2: '18:00', close2: '22:00' });
                const removeSlot2 = (day) => {
                  const current = getDay(day);
                  const { open2, close2, ...rest } = current;
                  setRestForm(p => ({ ...p, weekly_schedule: { ...(p.weekly_schedule || {}), [day]: rest } }));
                };
                const timeSx = { flex: 1, '& input': { fontSize: '0.8rem', py: '5px' } };
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    {DAYS.map(day => {
                      const d = getDay(day);
                      const slot2 = hasSlot2(d);
                      return (
                        <Box key={day} sx={{
                          py: 0.8, px: 1.2, borderRadius: '8px',
                          border: `1px solid ${d.closed ? P.line : P.purple + '44'}`,
                          background: d.closed ? 'transparent' : 'rgba(85,37,131,0.025)',
                        }}>
                          {/* Ligne principale */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 72, flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: d.closed ? P.muted : P.text, textTransform: 'capitalize' }}>
                                {day.charAt(0).toUpperCase() + day.slice(1)}
                              </Typography>
                            </Box>
                            <Switch size="small" checked={!d.closed}
                              onChange={e => setDay(day, { closed: !e.target.checked })}
                              sx={{ flexShrink: 0, '& .MuiSwitch-thumb': { bgcolor: !d.closed ? P.purple : undefined } }} />
                            {d.closed ? (
                              <Typography sx={{ fontSize: '0.75rem', color: P.muted, fontStyle: 'italic', ml: 0.5 }}>Fermé</Typography>
                            ) : (
                              <>
                                <TextField size="small" type="time" value={d.open || DEFAULT_OPEN}
                                  onChange={e => setDay(day, { open: e.target.value })}
                                  InputLabelProps={{ shrink: true }} sx={timeSx} />
                                <Typography sx={{ color: P.muted, fontSize: '0.85rem', flexShrink: 0 }}>–</Typography>
                                <TextField size="small" type="time" value={d.close || DEFAULT_CLOSE}
                                  onChange={e => setDay(day, { close: e.target.value })}
                                  InputLabelProps={{ shrink: true }} sx={timeSx} />
                                {!slot2 ? (
                                  <Tooltip title="Ajouter un 2ème créneau">
                                    <IconButton size="small" onClick={() => addSlot2(day)} sx={{ color: P.purple, flexShrink: 0 }}><AddIcon fontSize="small" /></IconButton>
                                  </Tooltip>
                                ) : (
                                  <Box sx={{ width: 28, flexShrink: 0 }} />
                                )}
                              </>
                            )}
                          </Box>
                          {/* 2ème créneau */}
                          {!d.closed && slot2 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.6, pl: '96px' }}>
                              <TextField size="small" type="time" value={d.open2}
                                onChange={e => setDay(day, { open2: e.target.value })}
                                InputLabelProps={{ shrink: true }} sx={timeSx} />
                              <Typography sx={{ color: P.muted, fontSize: '0.85rem', flexShrink: 0 }}>–</Typography>
                              <TextField size="small" type="time" value={d.close2}
                                onChange={e => setDay(day, { close2: e.target.value })}
                                InputLabelProps={{ shrink: true }} sx={timeSx} />
                              <Tooltip title="Supprimer ce créneau">
                                <IconButton size="small" onClick={() => removeSlot2(day)} sx={{ color: P.red, flexShrink: 0 }}><DeleteIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Box sx={{ py: 1.2, px: 2, borderLeft: `3px solid ${P.purple}`, background: 'rgba(85,37,131,0.03)' }}>
                <Typography sx={{ fontSize: '0.74rem', color: P.soft }}>
                  Frais de livraison calcules automatiquement : <strong>2,50 EUR par 5 km</strong>
                </Typography>
              </Box>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth label="Description" size="small" multiline rows={2} value={restForm.description||''} onChange={e => setRestForm(p=>({...p,description:e.target.value}))} sx={fieldSx} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setSettingsDialog(false)} sx={{ color: P.muted, textTransform: 'none', fontSize: '0.82rem' }}>Annuler</Button>
          <Button onClick={handleSaveSettings}
            sx={{ py: 0.8, px: 2.5, borderRadius: '5px', textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', background: P.text, color: P.white, '&:hover': { background: P.text, opacity: 0.85 } }}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  /* ── Sidebar nav items ───────────────────────────────────────────────────── */
  const NAV_SECTIONS = [
    {
      items: [
        { label: 'Accueil', idx: 6, icon: <HomeIcon sx={{ fontSize: 17 }} /> },
      ],
    },
    {
      label: 'Performance',
      items: [
        { label: 'Analytiques', idx: 3, icon: <BarChartIcon sx={{ fontSize: 17 }} /> },
        { label: 'Avis clients', idx: 5, icon: <StarIcon sx={{ fontSize: 17 }} />, badge: reviewStats?.pending_replies > 0 ? reviewStats.pending_replies : null },
        { label: 'Top Plats', idx: 7, icon: <EmojiEventsIcon sx={{ fontSize: 17 }} /> },
        { label: 'Rapports', idx: 8, icon: <AssessmentIcon sx={{ fontSize: 17 }} /> },
      ],
    },
    {
      label: 'Restaurant',
      items: [
        { label: 'Commandes', idx: 0, icon: <ReceiptLongIcon sx={{ fontSize: 17 }} />, badge: pendingOrders.length > 0 ? pendingOrders.length : null },
        { label: 'Menu', idx: 1, icon: <RestaurantMenuIcon sx={{ fontSize: 17 }} /> },
        { label: 'Options des plats', idx: 13, icon: <TuneIcon sx={{ fontSize: 17 }} /> },
        { label: 'Marketing', idx: 9, icon: <LocalOfferIcon sx={{ fontSize: 17 }} /> },
        { label: 'Horaires spéciaux', idx: 10, icon: <EventIcon sx={{ fontSize: 17 }} /> },
        { label: 'Personnalisation', idx: 2, icon: <PaletteIcon sx={{ fontSize: 17 }} /> },
      ],
    },
    {
      label: 'Équipe',
      items: [
        { label: 'Membres', idx: 11, icon: <GroupIcon sx={{ fontSize: 17 }} /> },
        { label: 'Documents', idx: 12, icon: <FolderIcon sx={{ fontSize: 17 }} /> },
      ],
    },
    {
      label: 'Compte',
      items: [
        { label: 'Paiement', idx: 4, icon: <PaymentIcon sx={{ fontSize: 17 }} /> },
      ],
    },
  ];

  /* ── Main render ─────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', background: P.bg, position: 'relative' }}>
      {/* Grain */}
      <Box sx={{ position: 'fixed', inset: 0, backgroundImage: GRAIN, backgroundRepeat: 'repeat', backgroundSize: '200px', pointerEvents: 'none', zIndex: 0 }} />

      {/* ── SIDEBAR ──────────────────────────────────────────────────────── */}
      <Box sx={{
        width: 220, flexShrink: 0, background: '#ffffff',
        borderRight: '1px solid #e8e8e8',
        position: 'sticky', top: 0, height: '100vh', overflowY: 'auto',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
        zIndex: 10,
      }}>
        {/* Restaurant info */}
        <Box sx={{ p: 2.5, borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {dashboard?.restaurant?.logo_url
              ? <Box component="img" src={dashboard.restaurant.logo_url} sx={{ width: 36, height: 36, borderRadius: '8px', objectFit: 'cover', flexShrink: 0, border: '1px solid #eee' }} />
              : <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: P.purple, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1rem' }}>{(dashboard?.restaurant?.name || 'R')[0]}</Typography>
                </Box>
            }
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {dashboard?.restaurant?.name || '…'}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>Mon restaurant</Typography>
            </Box>
          </Box>
        </Box>

        {/* Nav sections */}
        <Box sx={{ flex: 1, py: 1 }}>
          {NAV_SECTIONS.map((section, si) => (
            <Box key={si} sx={{ mb: 0.5 }}>
              {section.label && (
                <Typography sx={{ px: 2.5, py: 0.8, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#bbb' }}>
                  {section.label}
                </Typography>
              )}
              {section.items.map(item => {
                const active = tab === item.idx;
                return (
                  <Box key={item.idx} onClick={() => setTab(item.idx)} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2.5, py: 1.1, cursor: 'pointer',
                    bgcolor: active ? '#f5f5f5' : 'transparent',
                    borderLeft: active ? `3px solid ${P.purple}` : '3px solid transparent',
                    '&:hover': { bgcolor: '#f9f9f9' },
                    transition: 'all 0.12s',
                  }}>
                    <Box sx={{ color: active ? P.purple : '#888', display: 'flex' }}>{item.icon}</Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: active ? 700 : 500, color: active ? '#1a1a1a' : '#555', flex: 1 }}>
                      {item.label}
                    </Typography>
                    {item.badge && (
                      <Box sx={{ bgcolor: P.purple, color: 'white', borderRadius: '10px', px: 0.8, py: 0.1, fontSize: '0.65rem', fontWeight: 700, minWidth: 18, textAlign: 'center' }}>
                        {item.badge}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        {/* Bottom */}
        <Box sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Box onClick={() => setSettingsDialog(true)} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, cursor: 'pointer', borderRadius: '6px',
            '&:hover': { bgcolor: '#f5f5f5' },
          }}>
            <SettingsIcon sx={{ fontSize: 17, color: '#888' }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#555' }}>Paramètres</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── MOBILE/TABLET DRAWER ─────────────────────────────────────────── */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 260, background: '#ffffff', borderRight: `0.5px solid ${P.line}` } }}>
        {/* Drawer header */}
        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `0.5px solid ${P.line}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {dashboard?.restaurant?.logo_url
              ? <Box component="img" src={dashboard.restaurant.logo_url} sx={{ width: 32, height: 32, borderRadius: '6px', objectFit: 'cover', border: '1px solid #eee' }} />
              : <Box sx={{ width: 32, height: 32, borderRadius: '6px', background: P.purple, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.9rem' }}>{(dashboard?.restaurant?.name || 'R')[0]}</Typography>
                </Box>
            }
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
                {dashboard?.restaurant?.name || '…'}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: '#999' }}>Mon restaurant</Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: P.muted }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        {/* Drawer nav */}
        <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          {NAV_SECTIONS.map((section, si) => (
            <Box key={si} sx={{ mb: 0.5 }}>
              {section.label && (
                <Typography sx={{ px: 2.5, py: 0.8, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#bbb' }}>
                  {section.label}
                </Typography>
              )}
              {section.items.map(item => {
                const active = tab === item.idx;
                return (
                  <Box key={item.idx} onClick={() => { setTab(item.idx); setDrawerOpen(false); }} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2.5, py: 1.3, cursor: 'pointer',
                    bgcolor: active ? '#f5f5f5' : 'transparent',
                    borderLeft: active ? `3px solid ${P.purple}` : '3px solid transparent',
                    '&:hover': { bgcolor: '#f9f9f9' },
                    transition: 'all 0.12s',
                  }}>
                    <Box sx={{ color: active ? P.purple : '#888', display: 'flex' }}>{item.icon}</Box>
                    <Typography sx={{ fontSize: '0.84rem', fontWeight: active ? 700 : 500, color: active ? '#1a1a1a' : '#555', flex: 1 }}>
                      {item.label}
                    </Typography>
                    {item.badge && (
                      <Box sx={{ bgcolor: P.purple, color: 'white', borderRadius: '10px', px: 0.8, py: 0.1, fontSize: '0.65rem', fontWeight: 700, minWidth: 18, textAlign: 'center' }}>
                        {item.badge}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
        {/* Drawer bottom */}
        <Box sx={{ p: 2, borderTop: `0.5px solid ${P.line}` }}>
          <Box onClick={() => { setSettingsDialog(true); setDrawerOpen(false); }} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, cursor: 'pointer', borderRadius: '6px',
            '&:hover': { bgcolor: '#f5f5f5' },
          }}>
            <SettingsIcon sx={{ fontSize: 17, color: '#888' }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#555' }}>Paramètres</Typography>
          </Box>
        </Box>
      </Drawer>

      {/* ── MAIN CONTENT ─────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>

      {/* ── STICKY HEADER (mobile/tablet only) ───────────────────────────── */}
      <Box sx={{
        display: { xs: 'flex', md: 'none' },
        alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(228,240,246,0.95)', backdropFilter: 'blur(8px)',
        borderBottom: `0.5px solid ${P.line}`,
        px: { xs: 2, sm: 3 }, py: 1.2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <IconButton size="small" onClick={() => setDrawerOpen(true)}
            sx={{ border: `0.5px solid ${P.line}`, borderRadius: '6px', width: 34, height: 34, color: P.soft }}>
            <MenuIcon sx={{ fontSize: 18 }} />
          </IconButton>
          {dashboard?.restaurant?.logo_url && (
            <Box component="img" src={dashboard.restaurant.logo_url}
              sx={{ width: 28, height: 28, borderRadius: '5px', objectFit: 'cover', border: '1px solid #eee' }} />
          )}
          <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: P.text }}>
            {NAV_SECTIONS.flatMap(s => s.items).find(i => i.idx === tab)?.label || 'Dashboard'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          {resto && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.6,
              py: 0.4, px: 1.2, border: `0.5px solid ${resto.is_open ? P.green : P.line}`,
              borderRadius: '5px', background: resto.is_open ? P.greenBg : 'transparent',
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: resto.is_open ? P.green : P.muted, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: resto.is_open ? P.green : P.muted }}>
                {resto.is_open ? 'Ouvert' : 'Fermé'}
              </Typography>
            </Box>
          )}
          {pendingOrders.length > 0 && (
            <Box onClick={() => setTab(0)} sx={{
              bgcolor: P.orange, color: 'white', borderRadius: '10px',
              px: 0.8, py: 0.1, fontSize: '0.65rem', fontWeight: 800, cursor: 'pointer',
            }}>
              {pendingOrders.length}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2.5, sm: 4, md: 5 }, pt: { xs: 2.5, md: 4 }, pb: 10 }}>

        {/* ── Header editorial ──────────────────────────────────────────── */}
        <Box sx={{ mb: { xs: 3, md: 5 } }}>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box>
              <Typography sx={{ ...micro, mb: 1 }}>Espace restaurateur</Typography>
              {myRestaurants.length > 1 ? (
                <FormControl size="small" sx={{ minWidth: 240, mb: 0.5 }}>
                  <Select
                    value={selectedRestaurantId || (myRestaurants[0]?.id ?? '')}
                    onChange={(e) => setSelectedRestaurantId(e.target.value)}
                    displayEmpty
                    sx={{
                      fontWeight: 800, fontSize: { xs: '1.2rem', sm: '1.5rem' },
                      letterSpacing: '-0.03em', color: P.text,
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '& .MuiSelect-select': { px: 0, py: 0.3 },
                      '& .MuiSvgIcon-root': { color: P.muted },
                    }}
                  >
                    {myRestaurants.map(r => (
                      <MenuItem key={r.id} value={r.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: r.is_open ? P.green : P.red, flexShrink: 0 }} />
                          {r.name}
                          {!r.is_verified && <Typography component="span" sx={{ fontSize: '0.65rem', color: P.orange, fontWeight: 700, ml: 0.5 }}>Non vérifié</Typography>}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography sx={{
                  fontWeight: 800, fontSize: { xs: '1.6rem', sm: '2rem' },
                  letterSpacing: '-0.035em', lineHeight: 1.05, color: P.text,
                }}>
                  {resto.name}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                {resto.cuisine_type && (
                  <Typography sx={{ fontSize: '0.76rem', color: P.muted }}>{resto.cuisine_type}</Typography>
                )}
                {resto.city && (
                  <>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: P.muted, opacity: 0.4 }} />
                    <Typography sx={{ fontSize: '0.76rem', color: P.muted }}>{resto.city}</Typography>
                  </>
                )}
                {resto.rating > 0 && (
                  <>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: P.muted, opacity: 0.4 }} />
                    <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: P.gold }}>{Number(resto.rating).toFixed(1)}</Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title={resto.is_open
                ? `Ouvert${resto.opening_time && resto.closing_time ? ` (${resto.opening_time?.substring(0,5)} - ${resto.closing_time?.substring(0,5)})` : ''}${resto.opening_days ? ` -- ${(resto.opening_days || []).map(d => d.charAt(0).toUpperCase() + d.slice(1,3)).join(', ')}` : ''}`
                : 'Ferme -- hors horaires ou fermeture manuelle'}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.8,
                  py: 0.6, px: 1.5, border: `0.5px solid ${resto.is_open ? P.green : P.line}`, borderRadius: '5px',
                  background: resto.is_open ? P.greenBg : 'transparent',
                }}>
                  <StatusDot color={resto.is_open ? P.green : P.muted} label={resto.is_open ? 'Ouvert' : 'Ferme'} />
                  <Typography sx={{ fontSize: '0.68rem', color: P.muted, display: { xs: 'none', sm: 'block' } }}>
                    {resto.is_open ? 'Ouvert' : 'Fermé'}
                    {(() => {
                      const ws = resto.weekly_schedule;
                      if (ws) {
                        const days = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];
                        const today = days[new Date().getDay()];
                        const d = ws[today];
                        if (!d || d.closed) return ' · Fermé aujourd\'hui';
                        const s1 = `${d.open?.substring(0,5)}-${d.close?.substring(0,5)}`;
                        const s2 = d.open2 && d.close2 ? ` / ${d.open2.substring(0,5)}-${d.close2.substring(0,5)}` : '';
                        return ` · ${s1}${s2}`;
                      }
                      if (resto.opening_time && resto.closing_time)
                        return ` · ${resto.opening_time.substring(0,5)}-${resto.closing_time.substring(0,5)}`;
                      return '';
                    })()}
                  </Typography>
                </Box>
              </Tooltip>
              <Tooltip title={resto.is_open_manual ? 'Forcer la fermeture (jour ferie, etc.)' : 'Reactiver les horaires automatiques'}>
                <Button size="small" onClick={handleToggle}
                  sx={{
                    py: 0.5, px: 1.5, borderRadius: '5px', textTransform: 'none',
                    fontWeight: 700, fontSize: '0.7rem',
                    color: resto.is_open_manual ? P.red : P.green,
                    border: `0.5px solid ${resto.is_open_manual ? P.red : P.green}`,
                    background: 'transparent', minWidth: 0,
                    '&:hover': { background: resto.is_open_manual ? P.redBg : P.greenBg },
                  }}>
                  {resto.is_open_manual ? 'Fermer' : 'Rouvrir'}
                </Button>
              </Tooltip>
              <Tooltip title={resto.delivery_enabled !== false ? 'Suspendre les livraisons (rupture de matériel, etc.)' : 'Réactiver les livraisons'}>
                <Button size="small" onClick={handleToggleDelivery}
                  sx={{
                    py: 0.5, px: 1.5, borderRadius: '5px', textTransform: 'none',
                    fontWeight: 700, fontSize: '0.7rem',
                    color: resto.delivery_enabled !== false ? '#f57c00' : P.green,
                    border: `0.5px solid ${resto.delivery_enabled !== false ? '#f57c00' : P.green}`,
                    background: 'transparent', minWidth: 0,
                    '&:hover': { background: resto.delivery_enabled !== false ? '#fff3e0' : P.greenBg },
                  }}>
                  {resto.delivery_enabled !== false ? '🛵 Suspendre' : '🛵 Réactiver'}
                </Button>
              </Tooltip>
              <Tooltip title="Parametres">
                <IconButton onClick={() => setSettingsDialog(true)} size="small"
                  sx={{ border: `0.5px solid ${P.line}`, borderRadius: '5px', width: 34, height: 34, color: P.soft, '&:hover': { color: P.purple } }}>
                  <SettingsIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rafraichir">
                <IconButton onClick={() => { loadDashboard(); loadOrders(); }} size="small"
                  sx={{ border: `0.5px solid ${P.line}`, borderRadius: '5px', width: 34, height: 34, color: P.soft, '&:hover': { color: P.purple }, display: { xs: 'none', sm: 'flex' } }}>
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Stats row -- asymmetric */}
          <Box sx={{
            display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: { xs: 2, md: 3 },
            py: 2.5, borderTop: `0.5px solid ${P.line}`, borderBottom: `0.5px solid ${P.line}`,
          }}>
            <Stat label="Commandes du jour" value={fmtN(stats.today_orders)} accent={P.purple} />
            <Stat label="Vos gains aujourd'hui" value={`${fmt(stats.today_earnings)} €`} accent={P.green} />
            <Stat label="En attente" value={fmtN(stats.pending_orders)} accent={stats.pending_orders > 0 ? P.orange : P.muted} />
            <Stat label="Livrees" value={fmtN(stats.delivered_orders)} accent={P.green} />
          </Box>
        </Box>

        {/* ── Alerts ────────────────────────────────────────────────────── */}
        {error && (
          <Box sx={{ mb: 3, py: 1.2, px: 2, borderLeft: `3px solid ${P.red}`, background: P.redBg }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: P.red }}>{error}</Typography>
          </Box>
        )}
        {success && (
          <Box sx={{ mb: 3, py: 1.2, px: 2, borderLeft: `3px solid ${P.green}`, background: P.greenBg }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: P.green }}>{success}</Typography>
          </Box>
        )}

        {pendingOrders.length > 0 && (
          <Box sx={{ mb: 3, py: 1.2, px: 2, borderLeft: `3px solid ${P.orange}`, background: P.orangeBg }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: P.orange }}>
              {pendingOrders.length} commande{pendingOrders.length > 1 ? 's' : ''} en attente de confirmation
            </Typography>
          </Box>
        )}


        {/* ══ TAB 0 : COMMANDES ═══════════════════════════════════════════ */}
        {tab === 0 && (
          <Box>
            {activeOrders.length === 0 && completedOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography sx={{ fontSize: '0.84rem', color: P.muted, mb: 0.5 }}>Aucune commande pour l'instant</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>Les nouvelles commandes apparaitront ici</Typography>
              </Box>
            ) : (
              <>
                {activeOrders.length > 0 && (
                  <Box sx={{ mb: 5 }}>
                    <Typography sx={{ ...micro, mb: 2 }}>Commandes actives ({activeOrders.length})</Typography>
                    <Box sx={{ background: P.white, border: `0.5px solid ${P.line}`, borderRadius: '6px', overflow: 'hidden' }}>
                      {activeOrders.map(order => (
                        <OrderRow key={order.id} order={order} onUpdateStatus={handleOrderStatus} onCollected={handleOrderCollected} loading={orderLoad} />
                      ))}
                    </Box>
                  </Box>
                )}

                {completedOrders.length > 0 && (
                  <Box>
                    <Typography sx={{ ...micro, mb: 2 }}>Historique ({completedOrders.length})</Typography>
                    <Box sx={{ background: P.white, border: `0.5px solid ${P.line}`, borderRadius: '6px', overflow: 'hidden' }}>
                      <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 480 }}>
                          <TableHead>
                            <TableRow sx={{ '& th': { ...micro, py: 1.5, border: 'none', borderBottom: `0.5px solid ${P.lineFine}` } }}>
                              <TableCell>Commande</TableCell>
                              <TableCell>Client</TableCell>
                              <TableCell align="right">Montant</TableCell>
                              <TableCell align="center">Statut</TableCell>
                              <TableCell>Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {completedOrders.slice(0, 20).map(order => {
                              const s = STATUS[order.order_status] || {};
                              return (
                                <TableRow key={order.id} sx={{ '&:hover': { bgcolor: 'rgba(28,25,23,0.01)' }, '& td': { border: 'none', borderBottom: `0.5px solid ${P.lineFine}`, py: 1.3 } }}>
                                  <TableCell><Typography sx={{ ...mono, fontWeight: 800, fontSize: '0.8rem', color: P.text }}>#{order.order_number}</Typography></TableCell>
                                  <TableCell><Typography sx={{ fontSize: '0.8rem', color: P.soft }}>{order.customer_name}</Typography></TableCell>
                                  <TableCell align="right"><Typography sx={{ ...mono, fontWeight: 700, fontSize: '0.8rem', color: P.text }}>{fmt(order.total_amount)}</Typography></TableCell>
                                  <TableCell align="center"><StatusDot color={s.color} label={s.label} /></TableCell>
                                  <TableCell><Typography sx={{ fontSize: '0.72rem', color: P.muted }}>{fmtDate(order.created_at)}</Typography></TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* ══ TAB 1 : MENU ════════════════════════════════════════════════ */}
        {tab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: P.text, mb: 0.3 }}>Carte du restaurant</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>
                  {menuItems.length} article{menuItems.length > 1 ? 's' : ''} — {menuItems.filter(i=>i.is_available).length} disponibles
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<AddIcon />}
                  onClick={() => openDrinkDialog()}
                  sx={{
                    py: 1, px: { xs: 2, sm: 2.5 }, borderRadius: '8px', textTransform: 'none',
                    fontWeight: 700, fontSize: '0.88rem',
                    border: `1.5px solid ${P.purple}`, color: P.purple, bgcolor: 'transparent',
                    '&:hover': { bgcolor: `${P.purple}08` },
                  }}>
                  Boisson
                </Button>
                <Button startIcon={<AddIcon />}
                  onClick={() => openMenuDialog()}
                  sx={{
                    py: 1, px: { xs: 2, sm: 2.5 }, borderRadius: '8px', textTransform: 'none',
                    fontWeight: 700, fontSize: '0.88rem', background: P.text, color: P.white,
                    '&:hover': { background: P.text, opacity: 0.85 },
                  }}>
                  Ajouter
                </Button>
              </Box>
            </Box>

            {menuLoad ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={22} sx={{ color: P.purple }} /></Box>
            ) : categories.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography sx={{ fontSize: '0.84rem', color: P.muted, mb: 1 }}>Menu vide</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: P.muted, mb: 3 }}>Ajoutez vos premiers plats</Typography>
                <Button startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />} onClick={() => openMenuDialog()}
                  sx={{ py: 0.7, px: 2, borderRadius: '5px', textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', background: P.text, color: P.white, '&:hover': { background: P.text, opacity: 0.85 } }}>
                  Ajouter un plat
                </Button>
              </Box>
            ) : (
              categories.map(cat => (
                <Box key={cat} sx={{ mb: 5 }}
                  onDragOver={handleCatDragOver}
                  onDrop={e => handleCatDrop(e, cat)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: collapsedCats[cat] ? 0 : 2, cursor: 'pointer' }}
                    onClick={() => toggleCatCollapse(cat)}>
                    <Box draggable
                      onDragStart={e => { e.stopPropagation(); handleCatDragStart(e, cat); }}
                      onClick={e => e.stopPropagation()}
                      sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
                      <DragIndicatorIcon sx={{ fontSize: 16, color: P.muted }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: P.text, flex: 1 }}>{cat}</Typography>
                    <Typography sx={{ ...mono, fontSize: '0.66rem', fontWeight: 700, color: P.muted }}>
                      {menuItems.filter(i => i.category === cat).length}
                    </Typography>
                    <IconButton size="small" sx={{ p: 0.3 }}>
                      {collapsedCats[cat] ? <ExpandMoreIcon sx={{ fontSize: 18, color: P.muted }} /> : <ExpandLessIcon sx={{ fontSize: 18, color: P.muted }} />}
                    </IconButton>
                  </Box>

                  {!collapsedCats[cat] && <Box sx={{ background: P.white, border: `0.5px solid ${P.line}`, borderRadius: '6px', overflow: 'hidden' }}>
                    {menuItems.filter(i => i.category === cat).map((item, idx, arr) => (
                      <Box key={item.id} sx={{
                        py: 2, px: { xs: 2, sm: 3 },
                        borderBottom: idx < arr.length - 1 ? `0.5px solid ${P.lineFine}` : 'none',
                        opacity: item.is_available ? 1 : 0.5,
                        transition: 'opacity 0.15s',
                      }}>
                        {/* Top row: info + switch+actions */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.86rem', color: P.text }}>{item.name}</Typography>
                              {item.is_vegetarian && <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: P.green, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Veg</Typography>}
                              {!item.is_available && <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: P.red, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Indispo</Typography>}
                            </Box>
                            {item.description && (
                              <Typography sx={{ fontSize: '0.72rem', color: P.muted, lineHeight: 1.4, mb: 0.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{item.description}</Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Typography sx={{ ...mono, fontWeight: 800, fontSize: '0.88rem', color: P.purple }}>{fmt(item.price)}</Typography>
                              {item.preparation_time && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                  <AccessTimeIcon sx={{ fontSize: 11, color: P.muted }} />
                                  <Typography sx={{ fontSize: '0.68rem', color: P.muted }}>{item.preparation_time} min</Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                          {/* Switch + icon buttons (always visible) */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexShrink: 0 }}>
                            <Switch checked={!!item.is_available} onChange={() => handleToggleAvailable(item)}
                              sx={{ '& .Mui-checked': { color: P.green }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e !important' } }} />
                            <IconButton onClick={() => openMenuDialog(item)}
                              sx={{ width: 38, height: 38, border: `1px solid ${P.line}`, borderRadius: '8px', color: P.soft, '&:hover': { color: P.blue, borderColor: P.blue, bgcolor: '#e3f0ff' } }}>
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton onClick={() => handleDeleteItem(item.id)}
                              sx={{ width: 38, height: 38, border: `1px solid ${P.line}`, borderRadius: '8px', color: P.soft, '&:hover': { color: P.red, borderColor: P.red, bgcolor: P.redBg } }}>
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </Box>
                        {/* Personnaliser button — full width on xs, inline on sm+ */}
                        <Box sx={{ mt: 1 }}>
                          <Button onClick={() => openOptionsDialog(item)}
                            startIcon={<TuneIcon />}
                            sx={{
                              py: 0.8, px: 1.8, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.82rem',
                              border: `1px solid ${item.option_groups?.length ? P.purple : P.line}`,
                              color: item.option_groups?.length ? P.purple : P.soft,
                              bgcolor: item.option_groups?.length ? `${P.purple}08` : 'transparent',
                              width: { xs: '100%', sm: 'auto' },
                              '&:hover': { color: P.purple, borderColor: P.purple, bgcolor: `${P.purple}10` },
                            }}>
                            Personnaliser{item.option_groups?.length ? ` (${item.option_groups.length})` : ''}
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>}
                </Box>
              ))
            )}
          </Box>
        )}

        {/* ══ TAB 2 : PERSONNALISATION ═══════════════════════════════════ */}
        {tab === 2 && (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 4, md: 6 }, alignItems: 'start' }}>
              {/* Left: form fields */}
              <Box>
                <Typography sx={{ ...micro, mb: 3 }}>Apparence de votre page</Typography>

                {/* Cover image upload */}
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: P.soft, mb: 1 }}>Image de couverture</Typography>
                  <Box sx={{
                    position: 'relative', height: 140, borderRadius: '8px', overflow: 'hidden',
                    border: `1.5px dashed ${restForm.cover_image ? 'transparent' : P.line}`,
                    background: restForm.cover_image ? `url(${restForm.cover_image}) center/cover` : P.surface,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    transition: 'border-color 0.15s',
                    '&:hover': { borderColor: restForm.cover_image ? 'transparent' : P.purple },
                  }}
                  onClick={() => document.getElementById('cover-upload')?.click()}>
                    {restForm.cover_image && (
                      <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.82rem' }}>Changer l'image</Typography>
                      </Box>
                    )}
                    {!restForm.cover_image && !uploadingCover && (
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: P.muted }}>Cliquer pour ajouter</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: P.muted, mt: 0.3 }}>JPG, PNG ou WebP -- max 5 Mo</Typography>
                      </Box>
                    )}
                    {uploadingCover && <CircularProgress size={24} sx={{ color: P.purple }} />}
                  </Box>
                  <input id="cover-upload" type="file" accept="image/jpeg,image/png,image/webp" hidden
                    onChange={e => { if (e.target.files?.[0]) handleImageUpload(e.target.files[0], 'cover'); e.target.value = ''; }} />
                  <Typography sx={{ fontSize: '0.68rem', color: P.muted, mt: 0.5 }}>Image grand format affichee en haut de votre page</Typography>
                </Box>

                {/* Logo upload */}
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: P.soft, mb: 1 }}>Logo</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 72, height: 72, borderRadius: '14px', overflow: 'hidden', flexShrink: 0,
                      border: `1.5px dashed ${restForm.logo_url ? 'transparent' : P.line}`,
                      background: restForm.logo_url ? `url(${restForm.logo_url}) center/cover` : P.surface,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      position: 'relative',
                      '&:hover': { borderColor: restForm.logo_url ? 'transparent' : P.purple },
                    }}
                    onClick={() => document.getElementById('logo-upload')?.click()}>
                      {restForm.logo_url && (
                        <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}>
                          <EditIcon sx={{ color: '#fff', fontSize: 18 }} />
                        </Box>
                      )}
                      {!restForm.logo_url && !uploadingLogo && (
                        <AddIcon sx={{ color: P.muted, fontSize: 24 }} />
                      )}
                      {uploadingLogo && <CircularProgress size={20} sx={{ color: P.purple }} />}
                    </Box>
                    <Box>
                      <Button size="small" onClick={() => document.getElementById('logo-upload')?.click()}
                        sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', color: P.purple }}>
                        {restForm.logo_url ? 'Changer le logo' : 'Ajouter un logo'}
                      </Button>
                      <Typography sx={{ fontSize: '0.68rem', color: P.muted }}>Logo carre affiche a cote du nom</Typography>
                    </Box>
                  </Box>
                  <input id="logo-upload" type="file" accept="image/jpeg,image/png,image/webp" hidden
                    onChange={e => { if (e.target.files?.[0]) handleImageUpload(e.target.files[0], 'logo'); e.target.value = ''; }} />
                </Box>

                <TextField fullWidth label="Slogan / Tagline" size="small"
                  value={restForm.tagline||''} onChange={e => setRestForm(p=>({...p,tagline:e.target.value}))}
                  placeholder="Les meilleurs burgers de la ville"
                  helperText="Court texte accrocheur visible sous le nom"
                  inputProps={{ maxLength: 200 }}
                  sx={{ ...fieldSx, mb: 2.5 }} />

                <TextField fullWidth label="Banniere promotionnelle" size="small"
                  value={restForm.banner_text||''} onChange={e => setRestForm(p=>({...p,banner_text:e.target.value}))}
                  placeholder="Livraison offerte ce weekend !"
                  helperText="Message temporaire affiche en haut de votre page"
                  inputProps={{ maxLength: 300 }}
                  sx={{ ...fieldSx, mb: 2.5 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: P.soft, mb: 1 }}>Couleur d'accent</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Violet (defaut)', value: '#552583' },
                      { label: 'Rouge', value: '#dc2626' },
                      { label: 'Bleu', value: '#2563eb' },
                      { label: 'Vert', value: '#16a34a' },
                      { label: 'Orange', value: '#ea580c' },
                      { label: 'Rose', value: '#db2777' },
                      { label: 'Noir', value: '#171717' },
                    ].map(c => (
                      <Box key={c.value} onClick={() => setRestForm(p=>({...p,accent_color:c.value}))}
                        sx={{
                          width: 36, height: 36, borderRadius: '8px', background: c.value, cursor: 'pointer',
                          border: (restForm.accent_color||'#552583') === c.value ? '2.5px solid #1c1917' : '1.5px solid rgba(0,0,0,0.1)',
                          transition: 'transform 0.1s, border 0.15s',
                          '&:hover': { transform: 'scale(1.1)' },
                        }}
                        title={c.label}
                      />
                    ))}
                  </Box>
                  <Typography sx={{ fontSize: '0.68rem', color: P.muted, mt: 0.8 }}>
                    Couleur utilisee pour les accents sur votre page publique
                  </Typography>
                </Box>

                <Button onClick={handleSaveSettings}
                  sx={{
                    py: 1.1, px: 3, borderRadius: '5px', textTransform: 'none',
                    fontWeight: 700, fontSize: '0.82rem', background: P.text, color: P.white,
                    '&:hover': { background: P.text, opacity: 0.85 },
                  }}>
                  Enregistrer
                </Button>
              </Box>

              {/* Right: preview */}
              <Box>
                <Typography sx={{ ...micro, mb: 3 }}>Apercu</Typography>
                <Box sx={{
                  borderRadius: '8px', border: `0.5px solid ${P.line}`, overflow: 'hidden', background: P.white,
                }}>
                  {/* Mini hero preview */}
                  <Box sx={{
                    height: 120,
                    background: restForm.cover_image
                      ? `url(${restForm.cover_image}) center/cover`
                      : `linear-gradient(135deg, ${restForm.accent_color || '#552583'} 0%, ${P.gold} 100%)`,
                    position: 'relative', display: 'flex', alignItems: 'flex-end', p: 2,
                  }}>
                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 20%, rgba(0,0,0,0.6) 100%)' }} />
                    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {restForm.logo_url ? (
                        <Box component="img" src={restForm.logo_url} alt="logo"
                          sx={{ width: 40, height: 40, borderRadius: '10px', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }} />
                      ) : (
                        <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, border: '2px solid rgba(255,255,255,0.3)' }}>
                          ?
                        </Box>
                      )}
                      <Box>
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.92rem', lineHeight: 1.1 }}>
                          {restForm.name || 'Nom du restaurant'}
                        </Typography>
                        {restForm.tagline && (
                          <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.68rem', mt: 0.2 }}>
                            {restForm.tagline}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Banner preview */}
                  {restForm.banner_text && (
                    <Box sx={{ py: 1, px: 2, background: `${restForm.accent_color || '#552583'}12`, borderBottom: `0.5px solid ${P.lineFine}` }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: restForm.accent_color || '#552583', textAlign: 'center' }}>
                        {restForm.banner_text}
                      </Typography>
                    </Box>
                  )}

                  {/* Placeholder content */}
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Box sx={{ width: 4, height: 16, borderRadius: 2, background: restForm.accent_color || '#552583' }} />
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: P.text }}>Carte du restaurant</Typography>
                    </Box>
                    {[1,2,3].map(i => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: `0.5px solid ${P.lineFine}` }}>
                        <Box>
                          <Box sx={{ width: 100 + i*20, height: 8, borderRadius: 4, background: P.lineFine, mb: 0.5 }} />
                          <Box sx={{ width: 60 + i*10, height: 6, borderRadius: 3, background: P.lineFine }} />
                        </Box>
                        <Box sx={{ width: 40, height: 8, borderRadius: 4, background: `${restForm.accent_color || '#552583'}30` }} />
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Button fullWidth component="a" href={`/restaurants/${resto?.id}`} target="_blank"
                    startIcon={<VisibilityIcon sx={{ fontSize: '14px !important' }} />}
                    sx={{
                      py: 0.8, borderRadius: '5px', textTransform: 'none',
                      fontWeight: 700, fontSize: '0.78rem', color: P.purple,
                      border: `0.5px solid ${P.purple}`, background: 'transparent',
                      '&:hover': { background: 'rgba(85,37,131,0.04)' },
                    }}>
                    Voir ma page publique
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* ══ TAB 3 : STATS ═══════════════════════════════════════════════ */}
        {tab === 3 && (
          <Box>
            <Box sx={{
              display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
              gap: { xs: 3, md: 4 }, mb: 5,
              py: 3, borderBottom: `0.5px solid ${P.line}`,
            }}>
              <Stat label="CA total" value={`${fmt(stats.total_revenue)}`} accent={P.text} />
              <Stat label="Commandes totales" value={fmtN(stats.total_orders)} accent={P.purple} />
              <Stat label="Livrees" value={fmtN(stats.delivered_orders)} accent={P.green} />
              <Stat label="Annulees" value={fmtN(stats.cancelled_orders)} accent={P.red} />
              <Stat label="Articles au menu" value={fmtN(stats.total_items)} accent={P.text} />
              <Stat label="Disponibles" value={fmtN(stats.available_items)} accent={P.green} />
            </Box>

            {/* Performance */}
            <Typography sx={{ ...micro, mb: 3 }}>Performance</Typography>
            {stats.total_orders > 0 ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: P.soft }}>Taux de livraison</Typography>
                    <Typography sx={{ ...mono, fontWeight: 800, fontSize: '1.1rem', color: P.green }}>{deliveryRate}%</Typography>
                  </Box>
                  <Box sx={{ height: 4, borderRadius: 2, bgcolor: P.lineFine, overflow: 'hidden' }}>
                    <Box sx={{ width: `${deliveryRate}%`, height: '100%', bgcolor: P.green, borderRadius: 2, transition: 'width 0.6s ease' }} />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: P.soft }}>Taux d'annulation</Typography>
                    <Typography sx={{ ...mono, fontWeight: 800, fontSize: '1.1rem', color: P.red }}>{cancelRate}%</Typography>
                  </Box>
                  <Box sx={{ height: 4, borderRadius: 2, bgcolor: P.lineFine, overflow: 'hidden' }}>
                    <Box sx={{ width: `${cancelRate}%`, height: '100%', bgcolor: P.red, borderRadius: 2, transition: 'width 0.6s ease' }} />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Typography sx={{ fontSize: '0.8rem', color: P.muted, py: 4, textAlign: 'center' }}>Aucune donnee disponible</Typography>
            )}
          </Box>
        )}

        {/* ══ TAB 4 : PAIEMENT ════════════════════════════════════════════ */}
        {tab === 4 && (
          <Box>
            <Box sx={{ mb: 5 }}>
              <StripeConnectSection commissionRate={15} commissionLabel="sur le sous-total des commandes" />
            </Box>
            <Box sx={{ height: '0.5px', background: P.line, mb: 5 }} />
            <IbanTab />
          </Box>
        )}

        {/* ══ TAB 5 : AVIS (style Uber Eats) ═════════════════════════════ */}
        {tab === 5 && (() => {
          const filtered = reviews.filter(r => {
            if (reviewFilter.stars && r.rating !== reviewFilter.stars) return false;
            if (reviewFilter.hasComment && !r.comment) return false;
            if (reviewFilter.replied === true && !r.reply) return false;
            if (reviewFilter.replied === false && r.reply) return false;
            return true;
          });
          const daysLeft = (createdAt) => {
            const diff = 14 - Math.floor((Date.now() - new Date(createdAt)) / 86400000);
            return diff > 0 ? diff : 0;
          };
          return (
            <Box>
              {/* Sub-tabs */}
              <Box sx={{ display: 'flex', gap: 0, borderBottom: '1px solid #e8e8e8', mb: 3 }}>
                {['Aperçu', 'Avis', 'Articles du menu'].map((label, i) => (
                  <Box key={i} onClick={() => setReviewSubTab(i)} sx={{
                    px: 2.5, py: 1.2, cursor: 'pointer', fontSize: '0.85rem', fontWeight: reviewSubTab === i ? 700 : 500,
                    color: reviewSubTab === i ? P.text : P.muted,
                    borderBottom: reviewSubTab === i ? `2px solid ${P.text}` : '2px solid transparent',
                    mb: '-1px',
                  }}>{label}</Box>
                ))}
              </Box>

              {reviewSubTab === 0 && (
                <Box>
                  {/* Stats cards */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
                    {[
                      { label: 'Avis clients', value: reviewStats?.total ?? 0 },
                      { label: 'Note moyenne', value: reviewStats ? `${reviewStats.average} / 5` : '— / 5' },
                      { label: 'En attente de réponse', value: reviewStats?.pending_replies ?? 0 },
                      { label: 'Répondus', value: reviewStats ? reviewStats.total - reviewStats.pending_replies : 0 },
                    ].map(s => (
                      <Box key={s.label} sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '8px', p: 2.5 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: P.muted, mb: 0.5 }}>{s.label}</Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: P.text }}>{s.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* Distribution */}
                  {reviewStats?.distribution && (
                    <Box sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '8px', p: 3, mb: 4, maxWidth: 480 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', mb: 2 }}>Distribution des notes</Typography>
                      {reviewStats.distribution.map(d => (
                        <Box key={d.stars} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: P.soft, minWidth: 22 }}>{d.stars}★</Typography>
                          <Box sx={{ flex: 1, height: 8, bgcolor: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', bgcolor: d.stars >= 4 ? '#22c55e' : d.stars === 3 ? P.gold : P.red, borderRadius: 4, width: reviewStats.total > 0 ? `${(d.count / reviewStats.total) * 100}%` : '0%', transition: 'width 0.4s' }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.72rem', color: P.muted, minWidth: 24, textAlign: 'right' }}>{d.count}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {reviewSubTab === 1 && (
                <Box>
                  {/* Filters */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    {[1,2,3,4,5].map(s => (
                      <Box key={s} onClick={() => setReviewFilter(f => ({ ...f, stars: f.stars === s ? null : s }))}
                        sx={{ px: 1.5, py: 0.5, borderRadius: '20px', border: `1px solid ${reviewFilter.stars === s ? P.text : '#ddd'}`, cursor: 'pointer', fontSize: '0.78rem', fontWeight: reviewFilter.stars === s ? 700 : 400, bgcolor: reviewFilter.stars === s ? P.text : 'white', color: reviewFilter.stars === s ? 'white' : P.soft, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {s}★
                      </Box>
                    ))}
                    <Box onClick={() => setReviewFilter(f => ({ ...f, hasComment: !f.hasComment }))}
                      sx={{ px: 1.5, py: 0.5, borderRadius: '20px', border: `1px solid ${reviewFilter.hasComment ? P.text : '#ddd'}`, cursor: 'pointer', fontSize: '0.78rem', fontWeight: reviewFilter.hasComment ? 700 : 400, bgcolor: reviewFilter.hasComment ? P.text : 'white', color: reviewFilter.hasComment ? 'white' : P.soft }}>
                      Avec commentaire
                    </Box>
                    <Box onClick={() => setReviewFilter(f => ({ ...f, replied: f.replied === false ? null : false }))}
                      sx={{ px: 1.5, py: 0.5, borderRadius: '20px', border: `1px solid ${reviewFilter.replied === false ? P.text : '#ddd'}`, cursor: 'pointer', fontSize: '0.78rem', fontWeight: reviewFilter.replied === false ? 700 : 400, bgcolor: reviewFilter.replied === false ? P.text : 'white', color: reviewFilter.replied === false ? 'white' : P.soft }}>
                      Sans réponse
                    </Box>
                    {(reviewFilter.stars || reviewFilter.hasComment || reviewFilter.replied !== null) && (
                      <Box onClick={() => setReviewFilter({ stars: null, hasComment: false, replied: null })}
                        sx={{ px: 1.5, py: 0.5, borderRadius: '20px', border: '1px solid #ddd', cursor: 'pointer', fontSize: '0.78rem', color: P.red }}>
                        Effacer filtres
                      </Box>
                    )}
                  </Box>

                  {/* Count */}
                  {reviewLoad ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={24} sx={{ color: P.purple }} /></Box>
                  ) : (
                    <>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 2 }}>{filtered.length} avis</Typography>
                      {filtered.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <Typography sx={{ fontSize: '0.84rem', color: P.muted }}>Aucun avis correspondant</Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          {filtered.map(r => {
                            const dl = daysLeft(r.created_at);
                            const initials = `${(r.first_name || '?')[0]}${(r.last_name || '')[0] || ''}`.toUpperCase();
                            return (
                              <Box key={r.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 2.5, borderBottom: '1px solid #f0f0f0' }}>
                                {/* Avatar */}
                                {r.profile_image
                                  ? <Box component="img" src={r.profile_image} sx={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                  : <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: P.soft }}>{initials}</Typography>
                                    </Box>
                                }
                                {/* Center content */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: P.text }}>{r.first_name} {r.last_name}</Typography>
                                  <Typography sx={{ fontSize: '0.75rem', color: P.muted, mb: 0.8 }}>{r.customer_order_count ? `${r.customer_order_count} commandes` : 'Nouveau client'}</Typography>
                                  <Box sx={{ display: 'flex', gap: 0.2, mb: 0.8 }}>
                                    {[1,2,3,4,5].map(s => <Box key={s} component="span" sx={{ fontSize: '1rem', color: s <= r.rating ? '#f59e0b' : '#e0e0e0' }}>★</Box>)}
                                  </Box>
                                  {r.comment && (
                                    <Typography sx={{ fontSize: '0.84rem', color: P.soft, mb: 1, lineHeight: 1.5 }}>{r.comment}</Typography>
                                  )}
                                  {r.reply && (
                                    <Box sx={{ mt: 1, pl: 1.5, borderLeft: `3px solid ${P.purple}`, bgcolor: 'rgba(85,37,131,0.03)', py: 0.8, pr: 1, borderRadius: '0 6px 6px 0' }}>
                                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: P.purple, mb: 0.3 }}>✓ Répondu</Typography>
                                      <Typography sx={{ fontSize: '0.78rem', color: P.soft }}>{r.reply}</Typography>
                                    </Box>
                                  )}
                                </Box>
                                {/* Right: date + total + action */}
                                <Box sx={{ flexShrink: 0, textAlign: 'right', minWidth: { xs: 90, sm: 130 } }}>
                                  <Typography sx={{ fontSize: '0.78rem', color: P.soft }}>Commandé le {new Date(r.created_at).toLocaleDateString('fr-FR')}</Typography>
                                  {r.total_amount && <Typography sx={{ fontSize: '0.78rem', color: P.soft, mb: 1.5 }}>Total {Number(r.total_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</Typography>}
                                  {r.reply ? (
                                    <Button size="small" onClick={() => { setReplyDialog(r); setReplyText(r.reply || ''); }}
                                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', color: P.soft, px: 2, py: 0.6, '&:hover': { bgcolor: '#f5f5f5' } }}>
                                      Modifier
                                    </Button>
                                  ) : (
                                    <>
                                      <Button size="small" variant="contained" onClick={() => { setReplyDialog(r); setReplyText(''); }}
                                        sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', bgcolor: '#1a1a1a', color: 'white', px: 2.5, py: 0.7, borderRadius: '6px', mb: 0.5, '&:hover': { bgcolor: '#333' }, display: 'block', width: '100%' }}>
                                        Répondre
                                      </Button>
                                      {dl > 0 && <Typography sx={{ fontSize: '0.68rem', color: P.muted }}>{dl} jour{dl > 1 ? 's' : ''} restant{dl > 1 ? 's' : ''}</Typography>}
                                      {dl === 0 && <Typography sx={{ fontSize: '0.68rem', color: P.red }}>Délai expiré</Typography>}
                                    </>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}

              {reviewSubTab === 2 && (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                  <Typography sx={{ fontSize: '0.84rem', color: P.muted }}>Analyse des articles bientôt disponible</Typography>
                </Box>
              )}
            </Box>
          );
        })()}

        {/* ══ TAB 6 : ACCUEIL ═════════════════════════════════════════════ */}
        {tab === 6 && (
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: P.text, mb: 0.5 }}>Accueil</Typography>
            <Typography sx={{ fontSize: '0.84rem', color: P.muted, mb: 4 }}>Vue d'ensemble de votre restaurant</Typography>
            {dashboard && (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4,1fr)' }, gap: 2, mb: 4 }}>
                  {[
                    { label: "Commandes aujourd'hui", value: fmtN(dashboard.stats?.today_orders), sub: null },
                    { label: "CA aujourd'hui", value: `${fmt(dashboard.stats?.today_revenue)} €`, sub: `Vos gains : ${fmt(dashboard.stats?.today_earnings)} €`, subColor: P.green },
                    { label: "En attente", value: fmtN(dashboard.stats?.pending_orders), sub: null },
                    { label: "CA total livré", value: `${fmt(dashboard.stats?.total_revenue)} €`, sub: `Vos gains nets : ${fmt(dashboard.stats?.total_earnings)} €`, subColor: P.green },
                  ].map(s => (
                    <Box key={s.label} sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', p: 2.5 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: P.muted, mb: 0.5 }}>{s.label}</Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: P.text }}>{s.value}</Typography>
                      {s.sub && <Typography sx={{ fontSize: '0.7rem', color: s.subColor, fontWeight: 600, mt: 0.3 }}>{s.sub}</Typography>}
                    </Box>
                  ))}
                </Box>
                <Box sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', p: 3 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', mb: 2 }}>Dernières commandes</Typography>
                  {orders.slice(0, 5).length === 0
                    ? <Typography sx={{ fontSize: '0.82rem', color: P.muted }}>Aucune commande récente</Typography>
                    : orders.slice(0, 5).map(o => {
                        const s = STATUS[o.order_status] || { label: o.order_status, color: P.muted };
                        return (
                          <Box key={o.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.2, borderBottom: '0.5px solid #f0f0f0' }}>
                            <Typography sx={{ fontSize: '0.82rem', color: P.text, fontWeight: 600 }}>#{o.order_number}</Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: P.soft }}>{o.customer_name}</Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: P.text, fontWeight: 700 }}>{fmt(o.total_amount)} €</Typography>
                            <Box sx={{ px: 1, py: 0.2, borderRadius: '4px', bgcolor: `${s.color}18` }}>
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: s.color }}>{s.label}</Typography>
                            </Box>
                          </Box>
                        );
                      })
                  }
                </Box>
              </>
            )}
          </Box>
        )}

        {/* ══ TAB 7 : TOP PLATS ═══════════════════════════════════════════ */}
        {tab === 7 && (
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: P.text, mb: 0.5 }}>Top Plats</Typography>
            <Typography sx={{ fontSize: '0.84rem', color: P.muted, mb: 4 }}>Vos articles les plus commandés</Typography>
            {topLoad ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={24} sx={{ color: P.purple }} /></Box>
            ) : topItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <EmojiEventsIcon sx={{ fontSize: 40, color: '#e0e0e0', mb: 2 }} />
                <Typography sx={{ fontSize: '0.84rem', color: P.muted }}>Données disponibles après les premières commandes</Typography>
              </Box>
            ) : (
              <Box sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', overflow: 'hidden' }}>
                {topItems.map((item, i) => (
                  <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, borderBottom: i < topItems.length - 1 ? '0.5px solid #f5f5f5' : 'none' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: i === 0 ? P.gold : P.muted, minWidth: 28 }}>#{i+1}</Typography>
                    {item.image_url && <Box component="img" src={item.image_url} sx={{ width: 44, height: 44, borderRadius: '8px', objectFit: 'cover' }} />}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: P.text }}>{item.name}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>{item.category}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: P.text }}>{item.order_count}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: P.muted }}>commandes</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: P.green }}>{fmt(item.price)} €</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ══ TAB 8 : RAPPORTS ════════════════════════════════════════════ */}
        {tab === 8 && (
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: P.text, mb: 0.5 }}>Rapports</Typography>
            <Typography sx={{ fontSize: '0.84rem', color: P.muted, mb: 4 }}>Exportez vos données</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {[
                { title: 'Commandes', desc: 'Toutes vos commandes avec statut, montant et date', action: exportOrdersCSV, loading: exportLoading },
              ].map(r => (
                <Box key={r.title} sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: P.text, mb: 0.3 }}>{r.title}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: P.muted }}>{r.desc}</Typography>
                  </Box>
                  <Button variant="outlined" size="small" startIcon={r.loading ? <CircularProgress size={14} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
                    onClick={r.action} disabled={r.loading}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderColor: '#ddd', color: P.text, borderRadius: '6px', ml: 2, flexShrink: 0, '&:hover': { borderColor: P.purple, color: P.purple } }}>
                    CSV
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ══ TAB 9 : MARKETING ═══════════════════════════════════════════ */}
        {tab === 9 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>

            {/* ── Annonces ───────────────────────────────────────────────── */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: P.text, mb: 0.3 }}>Annonces</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: P.muted }}>Informez vos clients : événements, fermetures, nouveautés</Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => { setEditAnnouncement(null); setAnnouncementForm({ title: '', content: '', type: 'info', starts_at: '', ends_at: '' }); setAnnouncementDialog(true); }}
                  sx={{ textTransform: 'none', fontWeight: 700, bgcolor: P.purple, color: 'white', borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: P.purpleD } }}>
                  Nouvelle annonce
                </Button>
              </Box>

              {announcementsLoad ? (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
              ) : announcements.length === 0 ? (
                <Box sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', textAlign: 'center', py: 6 }}>
                  <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>📢</Typography>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: P.text, mb: 0.5 }}>Aucune annonce</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: P.muted }}>Publiez une annonce pour informer vos clients</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {announcements.map(a => {
                    const TYPE_CONFIG = {
                      info:  { label: 'Info',      color: P.blue,   bg: 'rgba(29,78,216,0.06)',   emoji: 'ℹ️' },
                      promo: { label: 'Promo',     color: P.green,  bg: P.greenBg,                emoji: '🎉' },
                      event: { label: 'Événement', color: P.purple, bg: 'rgba(85,37,131,0.06)',   emoji: '📅' },
                      alert: { label: 'Alerte',    color: P.red,    bg: P.redBg,                  emoji: '⚠️' },
                    };
                    const cfg = TYPE_CONFIG[a.type] || TYPE_CONFIG.info;
                    return (
                      <Box key={a.id} sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', p: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                          <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                            <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
                              {cfg.emoji}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: P.text }}>{a.title}</Typography>
                                <Box sx={{ px: 1, py: 0.1, borderRadius: '4px', bgcolor: cfg.bg }}>
                                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: cfg.color, letterSpacing: '0.05em' }}>{cfg.label.toUpperCase()}</Typography>
                                </Box>
                                {!a.is_active && (
                                  <Box sx={{ px: 1, py: 0.1, borderRadius: '4px', bgcolor: '#f5f5f5' }}>
                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: P.muted }}>INACTIF</Typography>
                                  </Box>
                                )}
                              </Box>
                              {a.content && <Typography sx={{ fontSize: '0.8rem', color: P.soft, mb: 0.5 }}>{a.content}</Typography>}
                              <Typography sx={{ fontSize: '0.7rem', color: P.muted }}>
                                {a.starts_at ? `Du ${new Date(a.starts_at).toLocaleDateString('fr-FR')}` : 'Publiée'}
                                {a.ends_at ? ` au ${new Date(a.ends_at).toLocaleDateString('fr-FR')}` : ''}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <IconButton size="small" onClick={() => { setEditAnnouncement(a); setAnnouncementForm({ title: a.title, content: a.content || '', type: a.type, starts_at: a.starts_at ? a.starts_at.slice(0,10) : '', ends_at: a.ends_at ? a.ends_at.slice(0,10) : '' }); setAnnouncementDialog(true); }}
                              sx={{ color: P.muted, '&:hover': { color: P.purple } }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => deleteAnnouncement(a.id)} sx={{ color: P.muted, '&:hover': { color: P.red } }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* ── Codes promo ────────────────────────────────────────────── */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: P.text, mb: 0.3 }}>Codes promo</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: P.muted }}>Réductions pour fidéliser vos clients</Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPromoDialog(true)}
                  sx={{ textTransform: 'none', fontWeight: 700, bgcolor: P.text, color: 'white', borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: P.purpleD } }}>
                  Créer une promo
                </Button>
              </Box>
              {promos.length === 0 ? (
                <Box sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', textAlign: 'center', py: 6 }}>
                  <LocalOfferIcon sx={{ fontSize: 40, color: '#e0e0e0', mb: 2 }} />
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: P.text, mb: 0.5 }}>Aucune promotion active</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: P.muted }}>Créez des codes promo pour fidéliser vos clients</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {promos.map(p => (
                    <Box key={p.id} sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: P.purple, mb: 0.3, fontFamily: 'monospace', letterSpacing: '0.08em' }}>{p.code}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: P.soft }}>
                          {p.discount_type === 'percent' ? `${p.discount_value}% de réduction` : `${p.discount_value} € de réduction`}
                          {p.min_order ? ` · min. ${p.min_order} €` : ''}
                          {p.max_uses ? ` · max ${p.max_uses} utilisations` : ''}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: P.muted, mt: 0.3 }}>
                          {p.expires_at ? `Expire le ${new Date(p.expires_at).toLocaleDateString('fr-FR')}` : 'Sans expiration'}
                          {` · ${p.uses_count || 0} utilisé${(p.uses_count || 0) > 1 ? 's' : ''}`}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => deletePromo(p.id)} sx={{ color: P.red, '&:hover': { bgcolor: P.redBg } }}>
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

          </Box>
        )}

        {/* ══ TAB 10 : HORAIRES SPÉCIAUX ══════════════════════════════════ */}
        {tab === 10 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: P.text, mb: 0.5 }}>Horaires spéciaux</Typography>
                <Typography sx={{ fontSize: '0.84rem', color: P.muted }}>Jours fériés, fermetures exceptionnelles</Typography>
              </Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setShForm({ date: '', reason: '', is_closed: true, open_time: '', close_time: '' }); setSpecialHoursDialog(true); }}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: P.text, color: 'white', borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: P.purpleD } }}>
                Ajouter
              </Button>
            </Box>
            {specialHours.length === 0 ? (
              <Box sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', textAlign: 'center', py: 10 }}>
                <EventIcon sx={{ fontSize: 40, color: '#e0e0e0', mb: 2 }} />
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: P.text, mb: 0.5 }}>Aucun horaire spécial</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: P.muted }}>Ajoutez des jours de fermeture ou des horaires exceptionnels</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {specialHours.map((sh, i) => (
                  <Box key={i} sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: P.text }}>{new Date(sh.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: P.muted }}>{sh.reason || (sh.is_closed ? 'Fermé' : `${sh.open_time} - ${sh.close_time}`)}</Typography>
                    </Box>
                    <Box sx={{ px: 1.5, py: 0.4, borderRadius: '6px', bgcolor: sh.is_closed ? P.redBg : P.greenBg }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: sh.is_closed ? P.red : P.green }}>{sh.is_closed ? 'Fermé' : 'Ouvert'}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ══ TAB 11 : ÉQUIPE ═════════════════════════════════════════════ */}
        {tab === 11 && (
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: P.text, mb: 0.5 }}>Membres de l'équipe</Typography>
            <Typography sx={{ fontSize: '0.84rem', color: P.muted, mb: 4 }}>Gérez l'accès de votre personnel</Typography>
            <Box sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', textAlign: 'center', py: 10 }}>
              <GroupIcon sx={{ fontSize: 40, color: '#e0e0e0', mb: 2 }} />
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: P.text, mb: 0.5 }}>Fonctionnalité bientôt disponible</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: P.muted }}>Vous pourrez inviter des membres de votre équipe à accéder au dashboard</Typography>
            </Box>
          </Box>
        )}

        {/* ══ TAB 12 : DOCUMENTS ══════════════════════════════════════════ */}
        {tab === 13 && (() => {
          const OPTION_TEMPLATES = [
            { icon: '⊘', label: 'Taille', description: 'Petite, Moyenne, Grande, XL', group: { name: 'Taille', selection_type: 'single', min_select: 1, max_select: 1 }, choices: [{name:'Petite',price_delta:0},{name:'Moyenne',price_delta:2},{name:'Grande',price_delta:4},{name:'XL',price_delta:6}] },
            { icon: '+', label: 'Suppléments', description: 'Fromage extra, Olives, Champignons', group: { name: 'Suppléments', selection_type: 'multiple', min_select: 0, max_select: 5 }, choices: [{name:'Fromage extra',price_delta:1.5},{name:'Olives',price_delta:0.5},{name:'Champignons',price_delta:1},{name:'Oignons',price_delta:0.5}] },
            { icon: '∿', label: 'Sauce', description: 'Tomate, Barbecue, Blanche', group: { name: 'Sauce', selection_type: 'single', min_select: 0, max_select: 1 }, choices: [{name:'Tomate',price_delta:0},{name:'Barbecue',price_delta:0},{name:'Blanche',price_delta:0},{name:'Samouraï',price_delta:0}] },
            { icon: '🥩', label: 'Viande', description: 'Poulet, Bœuf, Mixte', group: { name: 'Viande', selection_type: 'single', min_select: 1, max_select: 1 }, choices: [{name:'Poulet',price_delta:0},{name:'Bœuf',price_delta:1},{name:'Mixte',price_delta:0.5},{name:'Végétarien',price_delta:0}] },
            { icon: '🥤', label: 'Boisson', description: 'Eau, Soda, Jus', group: { name: 'Boisson', selection_type: 'single', min_select: 0, max_select: 1 }, choices: [{name:'Eau',price_delta:0},{name:'Coca-Cola',price_delta:1},{name:'Jus d\'orange',price_delta:1.5}] },
          ];

          const dialogItem = optionDialogItem ? menuItems.find(i => i.id === optionDialogItem.id) || optionDialogItem : null;

          const applyTemplate = async (tmpl) => {
            setOptionLoading(true);
            try {
              const grp = await restaurantService.createOptionGroup(dialogItem.id, tmpl.group);
              for (const c of tmpl.choices) await restaurantService.createOptionChoice(grp.id, c);
              await loadMenu();
              setOptionDialogItem(prev => ({ ...prev, _refresh: Date.now() }));
            } catch { showError('Erreur création template'); }
            finally { setOptionLoading(false); }
          };

          const createBlankGroup = async () => {
            if (!newGroupName.trim()) return;
            setOptionLoading(true);
            try {
              await restaurantService.createOptionGroup(dialogItem.id, { name: newGroupName.trim(), selection_type: newGroupType, min_select: newGroupType === 'single' ? 1 : 0, max_select: newGroupType === 'single' ? 1 : 5 });
              await loadMenu();
              setNewGroupMode(false); setNewGroupName(''); setNewGroupType('single');
            } catch { showError('Erreur création groupe'); }
            finally { setOptionLoading(false); }
          };

          const addChoice = async (groupId) => {
            if (!newChoiceName.trim()) return;
            setOptionLoading(true);
            try {
              await restaurantService.createOptionChoice(groupId, { name: newChoiceName.trim(), price_delta: parseFloat(newChoicePrice) || 0 });
              await loadMenu();
              setAddingChoiceGroupId(null); setNewChoiceName(''); setNewChoicePrice('0');
            } catch { showError('Erreur ajout option'); }
            finally { setOptionLoading(false); }
          };

          const deleteGroup = async (groupId) => {
            setOptionLoading(true);
            try { await restaurantService.deleteOptionGroup(groupId); await loadMenu(); }
            catch { showError('Erreur suppression groupe'); }
            finally { setOptionLoading(false); }
          };

          const deleteChoice = async (choiceId, itemId, groupId) => {
            setOptionLoading(true);
            try {
              await restaurantService.deleteOptionChoice(choiceId);
              setMenuItems(prev => prev.map(it => it.id === itemId ? { ...it, option_groups: it.option_groups.map(g => g.id === groupId ? { ...g, choices: g.choices.filter(c => c.id !== choiceId) } : g) } : it));
            } catch { showError('Erreur suppression option'); }
            finally { setOptionLoading(false); }
          };

          return (
            <Box>
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: P.text, mb: 0.5 }}>Options des plats</Typography>
                <Typography sx={{ fontSize: '0.84rem', color: P.muted }}>Posez des questions à vos clients quand ils commandent (taille, sauce, viande...)</Typography>
              </Box>

              {menuItems.length === 0 ? (
                <Box sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', textAlign: 'center', py: 10 }}>
                  <TuneIcon sx={{ fontSize: 40, color: '#e0e0e0', mb: 2 }} />
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: P.text }}>Aucun plat dans le menu</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {menuItems.map(item => {
                    const groupCount = (item.option_groups || []).length;
                    return (
                      <Box key={item.id} sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2 }}>
                        {item.image_url && <Box component="img" src={item.image_url} sx={{ width: 44, height: 44, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: P.text }}>{item.name}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>{item.category} · {fmt(item.price)} €</Typography>
                        </Box>
                        {groupCount > 0 ? (
                          <Typography sx={{ fontSize: '0.72rem', color: P.purple, fontWeight: 600, flexShrink: 0 }}>{groupCount} question{groupCount > 1 ? 's' : ''}</Typography>
                        ) : (
                          <Typography sx={{ fontSize: '0.72rem', color: P.muted, flexShrink: 0 }}>Aucune option</Typography>
                        )}
                        <Button size="small" startIcon={<TuneIcon sx={{ fontSize: '0.9rem' }} />}
                          onClick={() => { setOptionDialogItem(item); setNewGroupMode(false); setAddingChoiceGroupId(null); }}
                          sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', color: P.purple, border: `1px solid ${P.purple}22`, borderRadius: '7px', px: 1.5, py: 0.5, flexShrink: 0, '&:hover': { background: `${P.purple}08` } }}>
                          Personnaliser
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* ── Dialog personnalisation ── */}
              <Dialog open={!!optionDialogItem} onClose={() => setOptionDialogItem(null)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
                {dialogItem && (
                  <>
                    <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: `${P.purple}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <TuneIcon sx={{ color: P.purple, fontSize: '1.2rem' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: P.text }}>Personnaliser : {dialogItem.name}</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: P.muted, mt: 0.3 }}>Posez des questions à vos clients quand ils commandent ce plat (taille, suppléments, sauce...)</Typography>
                      </Box>
                      <IconButton size="small" onClick={() => setOptionDialogItem(null)} sx={{ ml: 'auto', flexShrink: 0 }}><CloseIcon fontSize="small" /></IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ px: 3, py: 2 }}>
                      {/* Groupes existants */}
                      {(dialogItem.option_groups || []).length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          {(dialogItem.option_groups || []).map(group => (
                            <Box key={group.id} sx={{ mb: 2.5, p: 2, border: '1px solid #f0f0f0', borderRadius: '10px', bgcolor: '#fafafa' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Box>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: P.text }}>{group.name}</Typography>
                                  <Typography sx={{ fontSize: '0.7rem', color: P.muted }}>{group.selection_type === 'single' ? '1 choix' : `Plusieurs choix · max ${group.max_select}`} {group.min_select > 0 ? '· Obligatoire' : '· Optionnel'}</Typography>
                                </Box>
                                <IconButton size="small" onClick={() => deleteGroup(group.id)} sx={{ color: P.red }}><DeleteIcon fontSize="small" /></IconButton>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                {group.choices.map(choice => (
                                  <Box key={choice.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.8, px: 1.2, borderRadius: '7px', bgcolor: 'white', border: '1px solid #eee' }}>
                                    <Switch size="small" checked={choice.is_available !== false}
                                      onChange={async () => {
                                        try {
                                          const res = await restaurantService.toggleOptionChoice(choice.id);
                                          setMenuItems(prev => prev.map(it => it.id === dialogItem.id ? { ...it, option_groups: it.option_groups.map(g => g.id === group.id ? { ...g, choices: g.choices.map(c => c.id === choice.id ? { ...c, is_available: res.is_available } : c) } : g) } : it));
                                        } catch { showError('Erreur mise à jour'); }
                                      }} />
                                    <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 500, color: choice.is_available === false ? P.muted : P.text, textDecoration: choice.is_available === false ? 'line-through' : 'none' }}>{choice.name}</Typography>
                                    {parseFloat(choice.price_delta) > 0 && <Typography sx={{ fontSize: '0.75rem', color: P.muted }}>+{fmt(choice.price_delta)} €</Typography>}
                                    <IconButton size="small" onClick={() => deleteChoice(choice.id, dialogItem.id, group.id)} sx={{ color: '#ccc', '&:hover': { color: P.red } }}><DeleteIcon sx={{ fontSize: '0.9rem' }} /></IconButton>
                                  </Box>
                                ))}
                                {addingChoiceGroupId === group.id ? (
                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                                    <TextField size="small" placeholder="Nom de l'option" value={newChoiceName} onChange={e => setNewChoiceName(e.target.value)} sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '7px', fontSize: '0.83rem' } }} />
                                    <TextField size="small" placeholder="Prix" value={newChoicePrice} onChange={e => setNewChoicePrice(e.target.value)} type="number" sx={{ width: 80, '& .MuiOutlinedInput-root': { borderRadius: '7px', fontSize: '0.83rem' } }} />
                                    <Button size="small" onClick={() => addChoice(group.id)} disabled={optionLoading} sx={{ background: P.purple, color: 'white', borderRadius: '7px', textTransform: 'none', fontWeight: 700, px: 1.5, '&:hover': { background: P.purpleD } }}>Ajouter</Button>
                                    <IconButton size="small" onClick={() => { setAddingChoiceGroupId(null); setNewChoiceName(''); setNewChoicePrice('0'); }}><CloseIcon fontSize="small" /></IconButton>
                                  </Box>
                                ) : (
                                  <Button size="small" startIcon={<AddIcon />} onClick={() => { setAddingChoiceGroupId(group.id); setNewChoiceName(''); setNewChoicePrice('0'); }}
                                    sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.78rem', color: P.purple, fontWeight: 600, mt: 0.5 }}>
                                    Ajouter une option
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Zone vide : templates */}
                      {(dialogItem.option_groups || []).length === 0 && !newGroupMode && (
                        <>
                          <Box sx={{ textAlign: 'center', py: 2, mb: 3 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: P.text, mb: 0.5 }}>Aucune personnalisation pour ce plat</Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: P.muted }}>Commencez avec un exemple prêt à l'emploi, ou créez une question vierge si vous préférez tout configurer vous-même.</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: P.muted, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>Exemples prêts à l'emploi</Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 3 }}>
                            {OPTION_TEMPLATES.map(tmpl => (
                              <Box key={tmpl.label} onClick={() => !optionLoading && applyTemplate(tmpl)}
                                sx={{ p: 2, border: '1.5px solid #e8e8e8', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { borderColor: P.purple, background: `${P.purple}04` } }}>
                                <Typography sx={{ fontSize: '1rem', mb: 0.5 }}>{tmpl.icon}</Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: P.text, mb: 0.3 }}>{tmpl.label}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: P.muted, lineHeight: 1.3 }}>{tmpl.description}</Typography>
                              </Box>
                            ))}
                          </Box>
                          <Divider sx={{ mb: 2.5 }}><Typography sx={{ fontSize: '0.75rem', color: P.muted, px: 1 }}>ou</Typography></Divider>
                        </>
                      )}

                      {/* Créer question vierge */}
                      {!newGroupMode && (
                        <Button fullWidth startIcon={<AddIcon />} onClick={() => setNewGroupMode(true)}
                          sx={{ border: '1.5px dashed #d0d0d0', borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: '0.88rem', color: P.text, py: 1.5, '&:hover': { borderColor: P.purple, color: P.purple, background: `${P.purple}04` } }}>
                          Créer une question vierge
                        </Button>
                      )}

                      {/* Formulaire nouveau groupe */}
                      {newGroupMode && (
                        <Box sx={{ p: 2, border: `1.5px solid ${P.purple}44`, borderRadius: '10px', background: `${P.purple}04` }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: P.text, mb: 1.5 }}>Nouvelle question</Typography>
                          <TextField fullWidth size="small" label="Ex: Quelle sauce ?" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            {[{v:'single',l:'1 seul choix'},{v:'multiple',l:'Plusieurs choix'}].map(opt => (
                              <Box key={opt.v} onClick={() => setNewGroupType(opt.v)}
                                sx={{ flex: 1, py: 1, px: 1.5, borderRadius: '8px', border: `1.5px solid ${newGroupType === opt.v ? P.purple : '#e0e0e0'}`, cursor: 'pointer', textAlign: 'center', background: newGroupType === opt.v ? `${P.purple}08` : 'transparent' }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: newGroupType === opt.v ? P.purple : P.muted }}>{opt.l}</Typography>
                              </Box>
                            ))}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button fullWidth variant="contained" onClick={createBlankGroup} disabled={optionLoading || !newGroupName.trim()}
                              sx={{ background: P.purple, textTransform: 'none', fontWeight: 700, borderRadius: '8px', '&:hover': { background: P.purpleD } }}>
                              Créer
                            </Button>
                            <Button onClick={() => { setNewGroupMode(false); setNewGroupName(''); }}
                              sx={{ textTransform: 'none', fontWeight: 700, color: P.muted, borderRadius: '8px' }}>Annuler</Button>
                          </Box>
                        </Box>
                      )}
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '0.75rem', color: P.muted }}>Vos modifications sont sauvegardées automatiquement.</Typography>
                      <Button variant="contained" onClick={() => setOptionDialogItem(null)}
                        sx={{ background: P.text, color: 'white', textTransform: 'none', fontWeight: 700, borderRadius: '8px', px: 3, '&:hover': { background: P.soft } }}>
                        Terminer
                      </Button>
                    </DialogActions>
                  </>
                )}
              </Dialog>
            </Box>
          );
        })()}

        {tab === 12 && (
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: P.text, mb: 0.5 }}>Documents</Typography>
            <Typography sx={{ fontSize: '0.84rem', color: P.muted, mb: 4 }}>Vos documents administratifs</Typography>
            <Box sx={{ background: P.white, border: '1px solid #e8e8e8', borderRadius: '10px', textAlign: 'center', py: 10 }}>
              <FolderIcon sx={{ fontSize: 40, color: '#e0e0e0', mb: 2 }} />
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: P.text, mb: 0.5 }}>Aucun document</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: P.muted }}>Vos documents de partenariat seront accessibles ici</Typography>
            </Box>
          </Box>
        )}

      </Box>
      </Box>

      {/* ── Dialog annonce ───────────────────────────────────────────── */}
      <Dialog open={announcementDialog} onClose={() => setAnnouncementDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: P.text, pt: 2.5, pb: 0, px: 3 }}>
          {editAnnouncement ? 'Modifier l\'annonce' : 'Nouvelle annonce'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1, px: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField fullWidth size="small" label="Titre" placeholder="Ex : Fermé le 25 décembre" value={announcementForm.title}
            onChange={e => setAnnouncementForm(f => ({ ...f, title: e.target.value }))} sx={fieldSx} />
          <TextField fullWidth size="small" label="Message (optionnel)" multiline rows={3} value={announcementForm.content}
            onChange={e => setAnnouncementForm(f => ({ ...f, content: e.target.value }))} sx={fieldSx} />
          <TextField fullWidth size="small" select label="Type" value={announcementForm.type}
            onChange={e => setAnnouncementForm(f => ({ ...f, type: e.target.value }))} sx={fieldSx}>
            <MenuItem value="info">ℹ️ Information</MenuItem>
            <MenuItem value="promo">🎉 Promotion</MenuItem>
            <MenuItem value="event">📅 Événement</MenuItem>
            <MenuItem value="alert">⚠️ Alerte</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField fullWidth size="small" label="Date de début" type="date" value={announcementForm.starts_at}
              onChange={e => setAnnouncementForm(f => ({ ...f, starts_at: e.target.value }))} InputLabelProps={{ shrink: true }} sx={fieldSx} />
            <TextField fullWidth size="small" label="Date de fin" type="date" value={announcementForm.ends_at}
              onChange={e => setAnnouncementForm(f => ({ ...f, ends_at: e.target.value }))} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAnnouncementDialog(false)} sx={{ textTransform: 'none', color: P.muted }}>Annuler</Button>
          <Button variant="contained" onClick={saveAnnouncement} disabled={!announcementForm.title.trim()}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: P.purple, borderRadius: '8px', px: 3, '&:hover': { bgcolor: P.purpleD } }}>
            {editAnnouncement ? 'Enregistrer' : 'Publier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog promo ─────────────────────────────────────────────── */}
      <Dialog open={promoDialog} onClose={() => setPromoDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: P.text, pt: 2.5, pb: 0, px: 3 }}>Créer un code promo</DialogTitle>
        <DialogContent sx={{ px: 3, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth size="small" label="Code promo" placeholder="BIENVENUE10" value={promoForm.code} onChange={e => setPromoForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} sx={fieldSx} />
            <TextField fullWidth size="small" select label="Type de réduction" value={promoForm.discount_type} onChange={e => setPromoForm(p => ({ ...p, discount_type: e.target.value }))} sx={fieldSx}>
              <option value="percent">Pourcentage (%)</option>
              <option value="fixed">Montant fixe (€)</option>
            </TextField>
            <TextField fullWidth size="small" label={promoForm.discount_type === 'percent' ? 'Réduction (%)' : 'Réduction (€)'} type="number" value={promoForm.discount_value} onChange={e => setPromoForm(p => ({ ...p, discount_value: e.target.value }))} sx={fieldSx} />
            <TextField fullWidth size="small" label="Commande minimum (€)" type="number" value={promoForm.min_order} onChange={e => setPromoForm(p => ({ ...p, min_order: e.target.value }))} sx={fieldSx} />
            <TextField fullWidth size="small" label="Date d'expiration" type="date" value={promoForm.expires_at} onChange={e => setPromoForm(p => ({ ...p, expires_at: e.target.value }))} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setPromoDialog(false)} sx={{ textTransform: 'none', color: P.muted }}>Annuler</Button>
          <Button variant="contained"
            onClick={async () => {
              try {
                await api.post('/restaurants/partner/promos', promoForm);
                setPromoDialog(false);
                loadPromos();
                showSuccess('Promo créée');
              } catch { showError('Erreur création promo'); }
            }}
            disabled={!promoForm.code || !promoForm.discount_value}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: P.text, '&:hover': { bgcolor: '#333' }, borderRadius: '6px' }}>
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog horaires spéciaux ─────────────────────────────────────── */}
      <Dialog open={specialHoursDialog} onClose={() => setSpecialHoursDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: P.text, pt: 2.5, pb: 0, px: 3 }}>Horaire spécial</DialogTitle>
        <DialogContent sx={{ px: 3, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth size="small" label="Date" type="date" value={shForm.date} onChange={e => setShForm(p => ({ ...p, date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={fieldSx} />
            <TextField fullWidth size="small" label="Motif (ex: Jour férié)" value={shForm.reason} onChange={e => setShForm(p => ({ ...p, reason: e.target.value }))} sx={fieldSx} />
            <FormControlLabel control={<Switch checked={shForm.is_closed} onChange={e => setShForm(p => ({ ...p, is_closed: e.target.checked }))} size="small" />} label={<Typography sx={{ fontSize: '0.84rem' }}>Fermé ce jour</Typography>} />
            {!shForm.is_closed && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField size="small" label="Ouverture" type="time" value={shForm.open_time} onChange={e => setShForm(p => ({ ...p, open_time: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, flex: 1 }} />
                <TextField size="small" label="Fermeture" type="time" value={shForm.close_time} onChange={e => setShForm(p => ({ ...p, close_time: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, flex: 1 }} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setSpecialHoursDialog(false)} sx={{ textTransform: 'none', color: P.muted }}>Annuler</Button>
          <Button variant="contained" onClick={() => { setSpecialHours(p => [...p, { ...shForm, id: Date.now() }]); setSpecialHoursDialog(false); showSuccess('Horaire ajouté'); }}
            disabled={!shForm.date}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: P.text, '&:hover': { bgcolor: '#333' }, borderRadius: '6px' }}>
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog répondre à un avis ───────────────────────────────────── */}
      <Dialog open={!!replyDialog} onClose={() => !replySaving && setReplyDialog(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '8px', border: `0.5px solid ${P.line}` } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: P.text, pb: 0, pt: 2.5, px: 3 }}>
          Répondre à l'avis
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: '16px !important' }}>
          {replyDialog && (
            <Box sx={{ mb: 2, p: 1.5, background: P.surface, borderRadius: '6px' }}>
              <Box sx={{ display: 'flex', gap: 0.3, mb: 0.5 }}>
                {[1,2,3,4,5].map(s => <Box key={s} component="span" sx={{ color: s <= replyDialog.rating ? P.gold : P.line }}>★</Box>)}
              </Box>
              <Typography sx={{ fontSize: '0.82rem', color: P.soft }}>{replyDialog.comment || '(sans commentaire)'}</Typography>
            </Box>
          )}
          <TextField fullWidth multiline rows={4} placeholder="Votre réponse au client..." value={replyText}
            onChange={e => setReplyText(e.target.value)} sx={fieldSx} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setReplyDialog(null)} disabled={replySaving}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', color: P.muted }}>
            Annuler
          </Button>
          <Button variant="contained" disabled={replySaving || !replyText.trim()}
            onClick={async () => {
              setReplySaving(true);
              try {
                await api.post(`/restaurants/partner/reviews/${replyDialog.id}/reply`, { reply: replyText });
                setReplyDialog(null);
                loadReviews();
                showSuccess('Réponse publiée');
              } catch { showError('Erreur lors de la réponse'); }
              finally { setReplySaving(false); }
            }}
            sx={{ background: P.purple, textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', px: 3, borderRadius: '6px', '&:hover': { background: P.purpleD } }}>
            {replySaving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Publier la réponse'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog ajouter/modifier article ────────────────────────────── */}
      <Dialog open={menuDialog} onClose={() => setMenuDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '8px', border: `0.5px solid ${P.line}` } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: P.text, pb: 0, pt: 2.5, px: 3 }}>
          {editItem ? 'Modifier l\'article' : 'Ajouter un plat'}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: '16px !important' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {/* Photo du plat */}
            <Box sx={{ gridColumn: '1 / -1', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: P.soft, mb: 1 }}>Photo du plat</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                {/* Miniature */}
                <Box sx={{
                  width: 100, height: 100, borderRadius: '8px', flexShrink: 0, overflow: 'hidden',
                  border: `1.5px dashed ${itemForm.image_url ? 'transparent' : P.line}`,
                  background: itemForm.image_url ? `url(${itemForm.image_url}) center/cover` : P.surface,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative', cursor: 'pointer',
                }}
                onClick={() => document.getElementById('dish-gallery-upload')?.click()}>
                  {!itemForm.image_url && !uploadingDish && (
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: P.muted, textAlign: 'center', px: 1 }}>Aucune photo</Typography>
                  )}
                  {uploadingDish && <CircularProgress size={20} sx={{ color: P.purple }} />}
                  {itemForm.image_url && !uploadingDish && (
                    <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}>
                      <Typography sx={{ color: '#fff', fontSize: '0.68rem', fontWeight: 700 }}>Changer</Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {/* Bouton galerie carré */}
                  <Box onClick={() => !uploadingDish && document.getElementById('dish-gallery-upload')?.click()}
                    sx={{
                      width: 100, height: 100, borderRadius: '8px', cursor: uploadingDish ? 'wait' : 'pointer',
                      border: `0.5px solid ${P.line}`, background: P.surface,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.8,
                      transition: 'all 0.15s', '&:hover': { borderColor: P.purple, background: 'rgba(85,37,131,0.03)' },
                    }}>
                    <PhotoLibraryIcon sx={{ fontSize: 22, color: P.muted }} />
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: P.soft }}>Galerie</Typography>
                  </Box>
                  {/* Bouton caméra carré */}
                  <Box onClick={() => !uploadingDish && document.getElementById('dish-camera-upload')?.click()}
                    sx={{
                      width: 100, height: 100, borderRadius: '8px', cursor: uploadingDish ? 'wait' : 'pointer',
                      border: `0.5px solid ${P.line}`, background: P.surface,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.8,
                      transition: 'all 0.15s', '&:hover': { borderColor: P.purple, background: 'rgba(85,37,131,0.03)' },
                    }}>
                    <CameraAltIcon sx={{ fontSize: 22, color: P.muted }} />
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: P.soft }}>Camera</Typography>
                  </Box>
                  {itemForm.image_url && (
                    <Box onClick={() => setItemForm(p => ({ ...p, image_url: '' }))}
                      sx={{
                        width: 100, height: 100, borderRadius: '8px', cursor: 'pointer',
                        border: `0.5px solid ${P.red}`, background: P.redBg,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.8,
                        transition: 'all 0.15s', '&:hover': { background: 'rgba(239,68,68,0.1)' },
                      }}>
                      <DeleteOutlineIcon sx={{ fontSize: 22, color: P.red }} />
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: P.red }}>Supprimer</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              {/* Input galerie (tous fichiers) */}
              <input id="dish-gallery-upload" type="file" accept="image/jpeg,image/png,image/webp" hidden
                onChange={e => { if (e.target.files?.[0]) handleDishImageUpload(e.target.files[0]); e.target.value = ''; }} />
              {/* Input caméra (ouvre directement l'appareil photo) */}
              <input id="dish-camera-upload" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden
                onChange={e => { if (e.target.files?.[0]) handleDishImageUpload(e.target.files[0]); e.target.value = ''; }} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth label="Nom du plat" size="small" value={itemForm.name}
                onChange={e => setItemForm(p => ({ ...p, name: e.target.value }))} sx={fieldSx} />
            </Box>
            <TextField fullWidth label="Categorie" size="small" value={itemForm.category}
              onChange={e => setItemForm(p => ({ ...p, category: e.target.value }))}
              placeholder="Entrees, Plats, Desserts..." sx={fieldSx} />
            <TextField fullWidth label="Prix (EUR)" size="small" type="number" value={itemForm.price}
              onChange={e => setItemForm(p => ({ ...p, price: e.target.value }))} sx={fieldSx} />
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth label="Description" size="small" multiline rows={2} value={itemForm.description}
                onChange={e => setItemForm(p => ({ ...p, description: e.target.value }))} sx={fieldSx} />
            </Box>
            <TextField fullWidth label="Temps de preparation (min)" size="small" type="number" value={itemForm.preparation_time}
              onChange={e => setItemForm(p => ({ ...p, preparation_time: e.target.value }))} sx={fieldSx} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, pt: 0.5 }}>
              <FormControlLabel
                control={<Switch size="small" checked={itemForm.is_vegetarian} onChange={e => setItemForm(p => ({ ...p, is_vegetarian: e.target.checked }))} />}
                label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Vegetarien</Typography>} />
              <FormControlLabel
                control={<Switch size="small" checked={itemForm.is_available} onChange={e => setItemForm(p => ({ ...p, is_available: e.target.checked }))}
                  sx={{ '& .Mui-checked': { color: P.green }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e !important' } }} />}
                label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Disponible</Typography>} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setMenuDialog(false)} sx={{ color: P.muted, textTransform: 'none', fontSize: '0.82rem' }}>Annuler</Button>
          <Button onClick={handleSaveItem} disabled={menuLoad}
            sx={{ py: 0.8, px: 2.5, borderRadius: '5px', textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', background: P.text, color: P.white, '&:hover': { background: P.text, opacity: 0.85 }, '&:disabled': { opacity: 0.3, background: P.text, color: P.white } }}>
            {menuLoad ? <CircularProgress size={16} sx={{ color: P.white }} /> : (editItem ? 'Enregistrer' : 'Ajouter')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ DIALOG BOISSON ══════════════════════════════════════════════ */}
      <Dialog open={drinkDialog} onClose={() => setDrinkDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '8px', border: `0.5px solid ${P.line}` } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: P.text, pb: 0, pt: 2.5, px: 3 }}>
          Ajouter une boisson
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Photo */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: P.soft, mb: 1 }}>Photo</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 100, height: 100, borderRadius: '8px', flexShrink: 0, overflow: 'hidden',
                  border: `1.5px dashed ${drinkForm.image_url ? 'transparent' : P.line}`,
                  background: drinkForm.image_url ? `url(${drinkForm.image_url}) center/cover` : P.surface,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative', cursor: 'pointer',
                }} onClick={() => document.getElementById('drink-gallery-upload')?.click()}>
                  {!drinkForm.image_url && !uploadingDish && <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: P.muted, textAlign: 'center', px: 1 }}>Aucune photo</Typography>}
                  {uploadingDish && <CircularProgress size={20} sx={{ color: P.purple }} />}
                  {drinkForm.image_url && !uploadingDish && (
                    <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}>
                      <Typography sx={{ color: '#fff', fontSize: '0.68rem', fontWeight: 700 }}>Changer</Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box onClick={() => !uploadingDish && document.getElementById('drink-gallery-upload')?.click()}
                    sx={{ width: 100, height: 100, borderRadius: '8px', cursor: 'pointer', border: `0.5px solid ${P.line}`, background: P.surface, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.8, '&:hover': { borderColor: P.purple } }}>
                    <PhotoLibraryIcon sx={{ fontSize: 22, color: P.muted }} />
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: P.soft }}>Galerie</Typography>
                  </Box>
                  <Box onClick={() => !uploadingDish && document.getElementById('drink-camera-upload')?.click()}
                    sx={{ width: 100, height: 100, borderRadius: '8px', cursor: 'pointer', border: `0.5px solid ${P.line}`, background: P.surface, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.8, '&:hover': { borderColor: P.purple } }}>
                    <CameraAltIcon sx={{ fontSize: 22, color: P.muted }} />
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: P.soft }}>Caméra</Typography>
                  </Box>
                </Box>
              </Box>
              <input id="drink-gallery-upload" type="file" accept="image/jpeg,image/png,image/webp" hidden
                onChange={e => {
                  if (!e.target.files?.[0]) return;
                  setUploadingDish(true);
                  const reader = new FileReader();
                  reader.onload = async () => {
                    try {
                      const { data } = await api.post('/restaurants/partner/upload-image', { file: reader.result, type: 'dish' });
                      if (data.url) setDrinkForm(p => ({ ...p, image_url: data.url }));
                    } catch { showError('Erreur upload image'); }
                    finally { setUploadingDish(false); }
                  };
                  reader.readAsDataURL(e.target.files[0]);
                  e.target.value = '';
                }} />
              <input id="drink-camera-upload" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden
                onChange={e => {
                  if (!e.target.files?.[0]) return;
                  setUploadingDish(true);
                  const reader = new FileReader();
                  reader.onload = async () => {
                    try {
                      const { data } = await api.post('/restaurants/partner/upload-image', { file: reader.result, type: 'dish' });
                      if (data.url) setDrinkForm(p => ({ ...p, image_url: data.url }));
                    } catch { showError('Erreur upload image'); }
                    finally { setUploadingDish(false); }
                  };
                  reader.readAsDataURL(e.target.files[0]);
                  e.target.value = '';
                }} />
            </Box>

            {/* Nom */}
            <TextField fullWidth label="Nom de la boisson" size="small" value={drinkForm.name}
              onChange={e => setDrinkForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Coca-Cola, Eau minérale, Café…" sx={fieldSx} />

            {/* Type de boisson */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: P.soft, mb: 1 }}>Type</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                {['Eau', 'Jus de fruit', 'Soda', 'Café / Thé', 'Alcool', 'Smoothie', 'Lait', 'Autre'].map(t => (
                  <Box key={t} onClick={() => setDrinkForm(p => ({ ...p, subcategory: p.subcategory === t ? '' : t }))}
                    sx={{
                      px: 1.8, py: 0.7, borderRadius: '20px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                      border: `1.5px solid ${drinkForm.subcategory === t ? P.purple : P.line}`,
                      bgcolor: drinkForm.subcategory === t ? `${P.purple}10` : 'transparent',
                      color: drinkForm.subcategory === t ? P.purple : P.soft,
                      transition: 'all 0.15s',
                    }}>
                    {t}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Température */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: P.soft, mb: 1 }}>Température</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[{ v: 'froid', label: '🧊 Froid' }, { v: 'chaud', label: '☕ Chaud' }, { v: 'les deux', label: '↕ Les deux' }].map(({ v, label }) => (
                  <Box key={v} onClick={() => setDrinkForm(p => ({ ...p, temperature: v }))}
                    sx={{
                      flex: 1, py: 1, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                      border: `1.5px solid ${drinkForm.temperature === v ? P.purple : P.line}`,
                      bgcolor: drinkForm.temperature === v ? `${P.purple}10` : 'transparent',
                      color: drinkForm.temperature === v ? P.purple : P.soft,
                      transition: 'all 0.15s',
                    }}>
                    {label}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Volume */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: P.soft, mb: 1 }}>Volume</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                {['25cl', '33cl', '50cl', '75cl', '1L', '2L', 'À la pression'].map(v => (
                  <Box key={v} onClick={() => setDrinkForm(p => ({ ...p, volume: p.volume === v ? '' : v }))}
                    sx={{
                      px: 1.8, py: 0.7, borderRadius: '20px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                      border: `1.5px solid ${drinkForm.volume === v ? P.purple : P.line}`,
                      bgcolor: drinkForm.volume === v ? `${P.purple}10` : 'transparent',
                      color: drinkForm.volume === v ? P.purple : P.soft,
                      transition: 'all 0.15s',
                    }}>
                    {v}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Prix + description */}
            <TextField fullWidth label="Prix (EUR)" size="small" type="number" value={drinkForm.price}
              onChange={e => setDrinkForm(p => ({ ...p, price: e.target.value }))} sx={fieldSx} />
            <TextField fullWidth label="Description (optionnel)" size="small" multiline rows={2} value={drinkForm.description}
              onChange={e => setDrinkForm(p => ({ ...p, description: e.target.value }))} sx={fieldSx} />

            {/* Disponible */}
            <FormControlLabel
              control={<Switch size="small" checked={drinkForm.is_available} onChange={e => setDrinkForm(p => ({ ...p, is_available: e.target.checked }))} sx={{ '& .MuiSwitch-thumb': { width: 14, height: 14 }, '& .MuiSwitch-track': { borderRadius: 7 }, '& .Mui-checked': { color: P.purple }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: P.purple } }} />}
              label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Disponible</Typography>} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDrinkDialog(false)} sx={{ color: P.muted, textTransform: 'none', fontSize: '0.82rem' }}>Annuler</Button>
          <Button onClick={handleSaveDrink} disabled={menuLoad}
            sx={{ py: 0.8, px: 2.5, borderRadius: '5px', textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', background: P.purple, color: P.white, '&:hover': { background: P.purple, opacity: 0.85 }, '&:disabled': { opacity: 0.3 } }}>
            {menuLoad ? <CircularProgress size={16} sx={{ color: P.white }} /> : 'Ajouter la boisson'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ DIALOG OPTIONS DE PERSONNALISATION ═══════════════════════════ */}
      <Dialog open={optionsDialog} onClose={() => setOptionsDialog(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '14px', background: P.surface, border: `0.5px solid ${P.line}`, boxShadow: '0 20px 60px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3.5, borderBottom: `0.5px solid ${P.lineFine}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: P.purple, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TuneIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', color: P.text, letterSpacing: '-0.01em' }}>
                Personnaliser : {optionsItem?.name}
              </Typography>
              <Typography sx={{ fontSize: '0.76rem', color: P.muted, fontWeight: 500 }}>
                Posez des questions à vos clients quand ils commandent ce plat (taille, suppléments, sauce…)
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3.5, pt: '20px !important', pb: 2 }}>
          {/* ── ETAT VIDE : templates + bouton vierge ── */}
          {optionsItem && optionsItem.option_groups.length === 0 && (
            <Box>
              <Box sx={{ textAlign: 'center', mb: 3, py: 2 }}>
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: P.text, mb: 0.5 }}>
                  Aucune personnalisation pour ce plat
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: P.muted, maxWidth: 480, mx: 'auto', lineHeight: 1.5 }}>
                  Commencez avec un exemple prêt à l'emploi, ou créez une question vierge si vous préférez tout configurer vous-même.
                </Typography>
              </Box>

              <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: P.muted, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>
                Exemples prêts à l'emploi
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 3 }}>
                {OPTION_TEMPLATES.map((t) => (
                  <Box key={t.key} onClick={() => !optionsBusy && handleApplyTemplate(t)}
                    sx={{
                      cursor: optionsBusy ? 'wait' : 'pointer',
                      p: 2, background: P.white, border: `0.5px solid ${P.line}`, borderRadius: '10px',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: P.purple, transform: 'translateY(-1px)', boxShadow: '0 4px 14px rgba(85,37,131,0.08)' },
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: '6px', background: `${P.purple}15`, color: P.purple, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: 900 }}>
                        {t.emoji}
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.86rem', color: P.text }}>{t.label}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: P.muted, lineHeight: 1.5 }}>
                      {t.choices.map(c => c.name).join(', ')}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ flex: 1, height: '0.5px', background: P.line }} />
                <Typography sx={{ fontSize: '0.7rem', color: P.muted, fontWeight: 600 }}>ou</Typography>
                <Box sx={{ flex: 1, height: '0.5px', background: P.line }} />
              </Box>

              <Button fullWidth startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={handleAddGroup} disabled={optionsBusy}
                sx={{
                  py: 1.3, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.84rem',
                  color: P.text, border: `0.5px dashed ${P.line}`, background: 'transparent',
                  '&:hover': { background: P.white, borderColor: P.purple, color: P.purple },
                }}>
                Créer une question vierge
              </Button>
            </Box>
          )}

          {/* ── LAYOUT EDITEUR + APERCU ── */}
          {optionsItem && optionsItem.option_groups.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 320px' }, gap: 3, alignItems: 'start' }}>
              {/* COLONNE GAUCHE : edition des groupes */}
              <Box>
                {optionsItem.option_groups.map((g, gIdx) => {
                  const isRequired = g.min_select >= 1;
                  return (
                    <Box key={g.id} sx={{ mb: 2.5, p: 2.5, background: P.white, border: `0.5px solid ${P.line}`, borderRadius: '10px' }}>
                      {/* En-tete : numero + suppression */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ ...mono, width: 22, height: 22, borderRadius: '5px', background: `${P.purple}10`, color: P.purple, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 900, flexShrink: 0 }}>
                          {gIdx + 1}
                        </Box>
                        <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: P.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          Question {gIdx + 1}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Supprimer cette question">
                          <IconButton size="small" disabled={optionsBusy} onClick={() => handleDeleteGroup(g.id)}
                            sx={{ width: 26, height: 26, color: P.muted, '&:hover': { color: P.red, background: P.redBg } }}>
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Champ : nom de la question */}
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: P.soft, mb: 0.6 }}>Texte de la question</Typography>
                        <TextField
                          fullWidth size="small" value={g.name}
                          onChange={(e) => refreshItemInList({ ...optionsItem, option_groups: optionsItem.option_groups.map(x => x.id === g.id ? { ...x, name: e.target.value } : x) })}
                          onBlur={(e) => handleUpdateGroup(g.id, { name: e.target.value })}
                          placeholder="Ex : Quelle taille ?"
                          sx={{ '& .MuiOutlinedInput-input': { fontWeight: 700, fontSize: '0.86rem', py: 1 } }}
                        />
                      </Box>

                      {/* Type de reponse : 1 choix / Plusieurs */}
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: P.soft, mb: 0.6 }}>Type de réponse</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {[
                            { k: 'single', label: 'Le client choisit 1 réponse', icon: '◉' },
                            { k: 'multiple', label: 'Le client peut en choisir plusieurs', icon: '☑' },
                          ].map(t => {
                            const active = g.selection_type === t.k;
                            return (
                              <Box key={t.k}
                                onClick={() => handleUpdateGroup(g.id, { selection_type: t.k, max_select: t.k === 'single' ? 1 : Math.max(1, g.max_select) })}
                                sx={{
                                  flex: 1, cursor: 'pointer', py: 1, px: 1.5, borderRadius: '6px',
                                  border: `1px solid ${active ? P.purple : P.line}`,
                                  background: active ? `${P.purple}06` : P.surface,
                                  display: 'flex', alignItems: 'center', gap: 1,
                                  transition: 'all 0.15s',
                                  '&:hover': { borderColor: P.purple },
                                }}>
                                <Box sx={{ fontSize: '0.95rem', color: active ? P.purple : P.muted, lineHeight: 1 }}>{t.icon}</Box>
                                <Typography sx={{ fontSize: '0.74rem', color: active ? P.text : P.muted, fontWeight: active ? 700 : 500, lineHeight: 1.3 }}>
                                  {t.label}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>

                      {/* Obligatoire / Facultatif */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch size="small" checked={isRequired}
                              onChange={(e) => handleUpdateGroup(g.id, { min_select: e.target.checked ? 1 : 0 })}
                              sx={{ '& .Mui-checked': { color: P.purple }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: `${P.purple} !important` } }} />
                          }
                          label={
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: P.text }}>
                              Réponse obligatoire
                            </Typography>
                          }
                        />
                        {g.selection_type === 'multiple' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '0.74rem', color: P.muted, fontWeight: 600 }}>Limite max :</Typography>
                            <TextField type="number" size="small" value={g.max_select}
                              onChange={(e) => refreshItemInList({ ...optionsItem, option_groups: optionsItem.option_groups.map(x => x.id === g.id ? { ...x, max_select: parseInt(e.target.value) || 1 } : x) })}
                              onBlur={(e) => handleUpdateGroup(g.id, { max_select: parseInt(e.target.value) || 1 })}
                              inputProps={{ min: 1, max: 20 }}
                              sx={{ width: 64, '& .MuiOutlinedInput-input': { fontSize: '0.78rem', py: 0.6, textAlign: 'center' } }} />
                          </Box>
                        )}
                      </Box>

                      {/* Liste des reponses (choix) */}
                      <Box sx={{ pt: 1.5, borderTop: `0.5px solid ${P.lineFine}` }}>
                        <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: P.muted, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
                          Réponses possibles
                        </Typography>
                        {g.choices.length === 0 && (
                          <Typography sx={{ fontSize: '0.74rem', color: P.muted, fontStyle: 'italic', mb: 1.2 }}>
                            Aucune réponse pour le moment. Ajoutez-en au moins une ci-dessous.
                          </Typography>
                        )}
                        {g.choices.map((c, cIdx) => (
                          <Box key={c.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                            <Box sx={{ ...mono, fontSize: '0.66rem', color: P.muted, width: 16, textAlign: 'right' }}>{cIdx + 1}.</Box>
                            <TextField fullWidth size="small" value={c.name}
                              onChange={(e) => refreshItemInList({ ...optionsItem, option_groups: optionsItem.option_groups.map(x => x.id === g.id ? { ...x, choices: x.choices.map(y => y.id === c.id ? { ...y, name: e.target.value } : y) } : x) })}
                              onBlur={(e) => handleUpdateChoice(g.id, c.id, { name: e.target.value })}
                              placeholder="Texte de la réponse"
                              sx={{ '& .MuiOutlinedInput-input': { fontSize: '0.82rem', py: 0.8 } }} />
                            <TextField size="small" value={c.price_delta}
                              onChange={(e) => refreshItemInList({ ...optionsItem, option_groups: optionsItem.option_groups.map(x => x.id === g.id ? { ...x, choices: x.choices.map(y => y.id === c.id ? { ...y, price_delta: e.target.value } : y) } : x) })}
                              onBlur={(e) => handleUpdateChoice(g.id, c.id, { price_delta: parseFloat(e.target.value) || 0 })}
                              type="number" inputProps={{ step: '0.50', min: 0 }}
                              InputProps={{
                                startAdornment: <Typography sx={{ fontSize: '0.7rem', color: P.muted, mr: 0.3 }}>+</Typography>,
                                endAdornment: <Typography sx={{ fontSize: '0.7rem', color: P.muted, ml: 0.3 }}>€</Typography>,
                              }}
                              sx={{ width: 110, '& .MuiOutlinedInput-input': { fontSize: '0.78rem', py: 0.8, textAlign: 'right' } }} />
                            <IconButton size="small" disabled={optionsBusy} onClick={() => handleDeleteChoice(g.id, c.id)}
                              sx={{ width: 24, height: 24, color: P.muted, '&:hover': { color: P.red, background: P.redBg } }}>
                              <DeleteIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Box>
                        ))}
                        <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />} onClick={() => handleAddChoice(g.id)} disabled={optionsBusy}
                          sx={{ color: P.purple, textTransform: 'none', fontSize: '0.74rem', fontWeight: 700, mt: 0.5, ml: 2.5 }}>
                          Ajouter une réponse
                        </Button>
                      </Box>
                    </Box>
                  );
                })}

                <Button fullWidth startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={handleAddGroup} disabled={optionsBusy}
                  sx={{
                    py: 1.2, mt: 0.5, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.82rem',
                    color: P.text, border: `0.5px dashed ${P.line}`, background: 'transparent',
                    '&:hover': { background: P.white, borderColor: P.purple, color: P.purple },
                  }}>
                  Ajouter une autre question
                </Button>
              </Box>

              {/* COLONNE DROITE : apercu cote client */}
              <Box sx={{ position: { md: 'sticky' }, top: { md: 0 } }}>
                <Box sx={{ p: 0.8, borderRadius: '14px', background: 'linear-gradient(135deg, #E4F0F6, #E4F0F6)', border: `0.5px solid ${P.line}` }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: P.muted, letterSpacing: '0.12em', textTransform: 'uppercase', textAlign: 'center', mt: 0.5, mb: 1 }}>
                    Aperçu côté client
                  </Typography>
                  <Box sx={{ background: P.white, borderRadius: '10px', p: 2, maxHeight: '60vh', overflowY: 'auto' }}>
                    <Typography sx={{ fontWeight: 900, fontSize: '0.92rem', color: P.text, mb: 0.3, letterSpacing: '-0.01em' }}>
                      {optionsItem.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.76rem', color: P.purple, fontWeight: 800, mb: 2 }}>
                      À partir de {fmt(optionsItem.price)} €
                    </Typography>

                    {optionsItem.option_groups.map((g) => {
                      const isRequired = g.min_select >= 1;
                      return (
                        <Box key={g.id} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.8 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: P.text }}>
                              {g.name || 'Question sans titre'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.62rem', color: isRequired ? P.red : P.muted, fontWeight: 700 }}>
                              {isRequired ? 'Requis' : 'Optionnel'}
                            </Typography>
                          </Box>
                          {g.choices.length === 0 && (
                            <Typography sx={{ fontSize: '0.7rem', color: P.muted, fontStyle: 'italic', pl: 1 }}>
                              (Aucune réponse pour le moment)
                            </Typography>
                          )}
                          {g.choices.map((c) => (
                            <Box key={c.id} sx={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              py: 0.8, px: 1.2, mb: 0.4, borderRadius: '7px',
                              border: `1px solid ${P.line}`, background: P.surface,
                            }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box sx={{
                                  width: 14, height: 14,
                                  borderRadius: g.selection_type === 'single' ? '50%' : '3px',
                                  border: `1.5px solid ${P.muted}`, flexShrink: 0,
                                }} />
                                <Typography sx={{ fontSize: '0.76rem', color: P.text }}>
                                  {c.name || 'Sans titre'}
                                </Typography>
                              </Box>
                              {parseFloat(c.price_delta) > 0 && (
                                <Typography sx={{ fontSize: '0.7rem', color: P.muted, fontWeight: 700 }}>
                                  +{fmt(c.price_delta)} €
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {optionsBusy && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', mt: 1.5 }}>
                    <CircularProgress size={12} sx={{ color: P.muted }} />
                    <Typography sx={{ fontSize: '0.7rem', color: P.muted, fontWeight: 600 }}>Sauvegarde…</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3.5, py: 2, gap: 1, borderTop: `0.5px solid ${P.lineFine}`, background: P.white }}>
          <Typography sx={{ fontSize: '0.72rem', color: P.muted, flex: 1 }}>
            Vos modifications sont sauvegardées automatiquement.
          </Typography>
          <Button onClick={() => setOptionsDialog(false)}
            sx={{ py: 1, px: 3, borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', background: P.text, color: P.white, '&:hover': { background: P.text, opacity: 0.85 } }}>
            Terminer
          </Button>
        </DialogActions>
      </Dialog>

      {renderSettingsDialog()}
    </Box>
  );
}
